import { StoredNextCheckIntentionSchema } from "./next-check.js";
export * from "./next-check.js";
import { OwnerLeadReturnMutationSchema } from "./owner-lead-return.js";
import { LeadMagnetSaveSchema, LeadMagnetStateMutationSchema, LeadMagnetPeriodSchema } from "./lead-magnets.js";
export * from "./lead-magnets.js";
export * from "./calendar-subscriptions.js";
export * from "./section-access.js";
export * from "./agent-connections.js";
import { EffectiveSectionAccessSchema } from "./section-access.js";
import { CampaignCohortPreviewInputSchema, CampaignCreateInputSchema, CampaignDeleteMutationSchema, CampaignDetailQuerySchema, CampaignEditInputSchema, CampaignSamplesMutationSchema, CampaignStateMutationSchema, } from "./campaigns.js";
import { EvaluationUsageSchema, emptyEvaluationUsage, SemanticExpectationSchema } from "./semantic-staging.js";
export * from "./semantic-staging.js";
import { z } from "zod";
export * from "./calendar-analytics.js";
import { ScenarioOperatingStateSchema } from "./agent-runtime.js";
import { ScheduleWorkspaceSchema } from "./scheduling.js";
import { ContactChannelHealthSchema } from "./contact-channels.js";
export * from "./contact-channels.js";
export * from "./agent-runtime.js";
export * from "./scheduling.js";
export * from "./native-calendar.js";
export * from "./qualification.js";
export * from "./acquisition.js";
export * from "./auto-messages.js";
export * from "./owner-lead-return.js";
export * from "./campaigns.js";
export * from "./webinar-analytics.js";
export * from "./analytics-core.js";
export * from "./analytics.js";
export * from "./robots.js";
export * from "./promises.js";
export * from "./alerts.js";
export * from "./bots.js";
// Zod otherwise probes `Function()` before falling back under strict CSP.
// Scope jitless mode to the browser so API and worker parsing keep the JIT path.
if (typeof document !== "undefined")
    z.config({ jitless: true });
const PasswordInputSchema = z
    .string()
    .min(1)
    .max(1_024)
    .refine((value) => new TextEncoder().encode(value).byteLength <= 1_024, {
    message: "Password must be at most 1024 UTF-8 bytes.",
});
const NewPasswordSchema = PasswordInputSchema.refine((value) => value.length >= 12, {
    message: "Password must contain at least 12 characters.",
});
export const MfaAuthorizationCodeSchema = z.string().trim().refine((value) => /^\d{6}$/u.test(value) || /^[a-f0-9]{5}(?:-[a-f0-9]{5}){3}$/u.test(value), { message: "Use a six-digit authenticator code or a recovery code." });
export const RoleSchema = z.enum(["owner", "admin", "manager", "reviewer"]);
export const ConnectorKindSchema = z.enum([
    "amocrm",
    "wazzup",
    "openai",
    "google_sheets",
]);
export const ConnectorStatusSchema = z.enum([
    "not_configured",
    "checking",
    "ready",
    "degraded",
    "error",
]);
export const DeliverySettingsSchema = z
    .object({
    deliveryEnabled: z.boolean(),
    autoDeliveryEnabled: z.boolean(),
    allowlistRequired: z.boolean(),
})
    .superRefine((settings, context) => {
    if (settings.autoDeliveryEnabled && !settings.deliveryEnabled) {
        context.addIssue({
            code: "custom",
            message: "Automatic delivery cannot be enabled while client delivery is disabled.",
            path: ["autoDeliveryEnabled"],
        });
    }
});
export const DeliveryModeSchema = z.enum(["preview", "review", "autonomous"]);
export function deliveryModeFor(settings) {
    if (!settings.deliveryEnabled)
        return "preview";
    return settings.autoDeliveryEnabled ? "autonomous" : "review";
}
export const SuggestedActionSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("none"),
        reason: z.string().min(1).max(500),
    }),
    z.object({
        type: z.literal("crm_note"),
        text: z.string().min(1).max(4_000),
    }),
    z.object({
        type: z.literal("crm_stage"),
        stageId: z.string().min(1).max(200),
        lossReasonId: z.number().int().positive().optional(),
        lossReasonName: z.string().trim().min(1).max(200).optional(),
        reason: z.string().min(1).max(500),
    }),
    z.object({
        type: z.literal("crm_task"),
        text: z.string().min(1).max(1_000),
        dueInMinutes: z.number().int().min(1).max(43_200),
    }),
    z.object({
        type: z.literal("crm_set_fields"),
        fields: z.array(z.object({
            fieldId: z.string().min(1).max(200),
            value: z.union([z.string().max(2_000), z.number(), z.boolean(), z.null()]),
        }).strict()).min(1).max(30),
    }),
    z.object({
        type: z.literal("crm_change_responsible"),
        userId: z.string().min(1).max(200),
        nativeConfirmation: z.object({ bookingRef: z.string().uuid(), expectedVersion: z.number().int().positive() }).strict().optional(),
    }),
    z.object({
        type: z.literal("booking"),
        service: z.string().min(1).max(300),
        requestedTime: z.string().max(300).nullable(),
        // Row label of the selected schedule manager. Older persisted proposals
        // omit it; a unique remaining candidate may still book, but several
        // managers at the same time cannot be chosen silently.
        managerLabel: z.string().trim().min(1).max(200).optional(),
        // VEHA, not the model, records the active immutable schedule version that
        // supplied this slot. Older persisted proposals omit it and remain
        // readable while the server keeps their legacy authorization path.
        bindingVersionId: z.string().uuid().optional(),
    }),
    z.object({
        type: z.literal("booking_cancel"),
        bookingRef: z.string().min(1).max(300),
        // The server pins a standalone native cancellation to its supplied version.
        expectedVersion: z.number().int().positive().optional(),
    }),
    z.object({
        type: z.literal("table_upsert"),
        bindingId: z.string().min(1).max(200),
        values: z.record(z.string().min(1).max(200), z.union([
            z.string().max(2_000),
            z.number(),
            z.boolean(),
            z.null(),
        ])),
    }),
    z.object({
        type: z.literal("send_media"),
        assetId: z.string().min(1).max(300),
        caption: z.string().max(2_000).nullable(),
    }),
    z.object({
        type: z.literal("reminder"),
        dueAt: z.string().datetime(),
        message: z.string().min(1).max(4_000),
    }),
]);
export const ScenarioActionTypeSchema = z.enum([
    "crm_note",
    "crm_stage",
    "crm_task",
    "crm_set_fields",
    "crm_change_responsible",
    "booking",
    "booking_cancel",
    "table_upsert",
    "send_media",
    "reminder",
]);
export const ScenarioActionTypesSchema = z
    .array(ScenarioActionTypeSchema)
    .max(10)
    .superRefine((actions, context) => {
    if (new Set(actions).size !== actions.length) {
        context.addIssue({
            code: "custom",
            message: "A scenario action cannot be selected more than once.",
        });
    }
});
export const LegacyScenarioActionTypeSchema = z.enum([
    "crm_note",
    "crm_stage",
    "booking",
    "booking_cancel",
    "send_media",
]);
const LegacyScenarioActionTypesSchema = z
    .array(LegacyScenarioActionTypeSchema)
    .max(5)
    .superRefine((actions, context) => {
    if (new Set(actions).size !== actions.length) {
        context.addIssue({
            code: "custom",
            message: "A legacy scenario action cannot be selected more than once.",
        });
    }
});
export const CompanyMediaKindSchema = z.enum([
    "audio",
    "image",
    "video",
    "document",
]);
export const CompanyMediaAssetMutationSchema = z.object({
    title: z.string().trim().min(2).max(200),
    mediaKind: CompanyMediaKindSchema,
    fileName: z.string().trim().min(1).max(240),
    contentType: z.string().trim().toLowerCase().regex(/^[a-z0-9][a-z0-9.+-]{0,126}\/[a-z0-9][a-z0-9.+-]{0,126}$/u),
    contentBase64: z.string().min(4).max(14_000_000).refine((value) => /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/u
        .test(value), { message: "Media content must use canonical base64." }),
}).strict();
export const WorkspaceCompanyMediaAssetSchema = z.object({
    id: z.string().uuid(),
    title: z.string().trim().min(2).max(200),
    mediaKind: CompanyMediaKindSchema,
    fileName: z.string().trim().min(1).max(240),
    contentType: z.string().trim().min(3).max(255),
    byteSize: z.number().int().min(1).max(10_485_760),
    contentSha256: z.string().regex(/^[0-9a-f]{64}$/u),
    active: z.boolean(),
    createdAt: z.string().datetime(),
    createdBy: z.string().trim().min(1).max(200),
}).strict();
export const ScenarioWorkflowMediaBindingSchema = z.object({
    key: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/u),
    assetId: z.string().uuid(),
    contentSha256: z.string().regex(/^[0-9a-f]{64}$/u),
    sourceOrder: z.number().int().min(0).max(39),
}).strict();
export const CompanyMediaAssetMutationResultSchema = z.object({
    mediaAssetId: z.string().uuid(),
    contentSha256: z.string().regex(/^[0-9a-f]{64}$/u),
    byteSize: z.number().int().min(1).max(10_485_760),
    createdAt: z.string().datetime(),
}).strict();
export const CompanyMediaAssetArchiveMutationSchema = z.object({
    reason: z.string().trim().min(5).max(1_000),
}).strict();
export const CompanyMediaAssetArchiveResultSchema = z.object({
    mediaAssetId: z.string().uuid(),
}).strict();
export const ScenarioWorkflowFollowUpStepSchema = z.object({
    offsetMinutes: z.number().int().min(1).max(43_200),
    message: z.string().trim().min(1).max(4_000),
    waitWhileStageId: z.string().uuid(),
    afterSendStageId: z.string().uuid().nullable(),
}).strict();
export const ScenarioWorkflowResolverFollowUpStepSchema = z.object({
    offsetMinutes: z.number().int().min(1).max(43_200),
    intention: z.string().trim().min(5).max(600),
    referenceTexts: z.array(z.string().trim().min(1).max(4_000)).max(6),
    waitWhileStageId: z.string().uuid(),
    afterSendStageId: z.string().uuid().nullable(),
}).strict();
export const ScenarioWorkflowFollowUpPolicySchema = z.discriminatedUnion("mode", [
    z.object({
        mode: z.literal("off"),
        steps: z.array(ScenarioWorkflowFollowUpStepSchema).length(0),
    }).strict(),
    z.object({
        mode: z.literal("silence"),
        steps: z.array(ScenarioWorkflowFollowUpStepSchema).min(1).max(10),
    }).strict(),
    z.object({
        mode: z.literal("resolver"),
        steps: z.array(ScenarioWorkflowResolverFollowUpStepSchema).min(1).max(10),
    }).strict(),
]).superRefine((policy, context) => {
    let previousOffset = 0;
    let expectedStageId = null;
    policy.steps.forEach((step, index) => {
        if (step.offsetMinutes <= previousOffset) {
            context.addIssue({
                code: "custom",
                path: ["steps", index, "offsetMinutes"],
                message: "Follow-up offsets must increase from the original message.",
            });
        }
        if (expectedStageId !== null && step.waitWhileStageId !== expectedStageId) {
            context.addIssue({
                code: "custom",
                path: ["steps", index, "waitWhileStageId"],
                message: "Each follow-up must wait in the stage left by the previous step.",
            });
        }
        previousOffset = step.offsetMinutes;
        expectedStageId = step.afterSendStageId ?? step.waitWhileStageId;
    });
});
export const ScenarioWorkingWindowSchema = z.discriminatedUnion("mode", [
    z.object({
        mode: z.literal("always"),
    }).strict(),
    z.object({
        mode: z.literal("daily"),
        timezone: z.string().trim().min(3).max(100),
        startsAt: z.string().regex(/^(?:[01][0-9]|2[0-3]):[0-5][0-9]$/u),
        endsAt: z.string().regex(/^(?:[01][0-9]|2[0-3]):[0-5][0-9]$/u),
    }).strict().refine((window) => window.startsAt !== window.endsAt, { message: "A daily working window must contain at least one working minute." }),
]);
export const ScenarioInboundBatchWindowSchema = z.discriminatedUnion("mode", [
    z.object({
        mode: z.literal("off"),
    }).strict(),
    z.object({
        mode: z.literal("silence"),
        seconds: z.number().int().min(1).max(3_600),
    }).strict(),
]);
export const ScenarioDeliveryWindowMutationSchema = z.object({
    expectedActivationId: z.string().uuid(),
    expectedScenarioVersionId: z.string().uuid(),
    expectedRevision: z.number().int().nonnegative(),
    workingWindow: ScenarioWorkingWindowSchema,
    batchWindow: ScenarioInboundBatchWindowSchema,
    idempotencyKey: z.string().trim().min(16).max(160),
}).strict();
export const ScenarioDeliveryWindowStateSchema = z.object({
    workingWindow: ScenarioWorkingWindowSchema,
    batchWindow: ScenarioInboundBatchWindowSchema,
    overridden: z.boolean(),
    revision: z.number().int().nonnegative(),
    changedAt: z.string().datetime().nullable(),
    changedBy: z.string().trim().min(1).max(200).nullable(),
}).strict();
export const ScenarioDeliveryWindowResultSchema = ScenarioDeliveryWindowStateSchema.extend({
    scenarioId: z.string().uuid(),
    replayed: z.boolean(),
}).strict();
const ScenarioResponsibleUserIdsSchema = z.array(z.string().uuid()).min(1).max(100)
    .refine((values) => new Set(values).size === values.length, {
    message: "Responsible CRM users must be unique.",
});
export const ScenarioResponsibleScopeSchema = z.discriminatedUnion("mode", [
    z.object({ mode: z.literal("all"), crmUserIds: z.array(z.never()).length(0) }).strict(),
    z.object({ mode: z.literal("selected"), crmUserIds: ScenarioResponsibleUserIdsSchema }).strict(),
]);
export const ScenarioResponsibleScopeMutationSchema = z.object({
    expectedActivationId: z.string().uuid(),
    expectedScenarioVersionId: z.string().uuid(),
    expectedRevision: z.number().int().nonnegative(),
    scope: ScenarioResponsibleScopeSchema,
    idempotencyKey: z.string().trim().min(16).max(160),
}).strict();
export const ScenarioResponsibleScopeStateSchema = z.object({
    scope: ScenarioResponsibleScopeSchema,
    revision: z.number().int().nonnegative(),
    changedAt: z.string().datetime().nullable(),
    changedBy: z.string().trim().min(1).max(200).nullable(),
}).strict();
export const ScenarioResponsibleScopeResultSchema = ScenarioResponsibleScopeStateSchema.extend({
    scenarioId: z.string().uuid(),
    replayed: z.boolean(),
}).strict();
export const ScenarioWorkflowResponsePolicySchema = z.object({
    initialDelaySeconds: z.number().int().min(0).max(3_600),
    workingWindow: ScenarioWorkingWindowSchema,
}).strict();
export const DraftMessagePartsSchema = z
    .array(z.string().min(1).max(4_000))
    .min(1)
    .max(3);
export function normalizeCustomerFacingPunctuation(text) {
    return text.replace(/[\u2013\u2014]/gu, "-");
}
export const AvailableDeliveryTargetSchema = z.object({
    targetId: z.string().uuid(),
    kind: z.enum(["existing_conversation", "initiate"]),
    channelType: z.enum(["whatsapp", "telegram"]),
    channelLabel: z.string().trim().min(1).max(100),
    isCurrent: z.boolean(),
    canSelect: z.boolean(),
    conversationId: z.string().uuid().nullable(),
    conversationRevision: z.number().int().nonnegative().nullable(),
    managerFence: z.number().int().nonnegative().nullable(),
    identityKind: z.enum(["existing_chat", "telegram_username", "phone"]),
    identityEvidenceMasked: z.string().trim().min(1).max(80),
    // Present only when Wazzup reports the target's line down (vekha.wazzup_line_down).
    lineDown: z.literal(true).optional(),
    health: ContactChannelHealthSchema.optional(),
}).strict();
export const ReplyChannelDirectiveSchema = z.object({
    action: z.enum(["current", "switch", "initiate"]),
    targetId: z.string().uuid().nullable().describe("Exact supplied availableDeliveryTargets.id. Required when action is switch or initiate; must be null when action is current.").default(null),
    conversationId: z.string().uuid().nullable().describe("Existing conversation only. Required when action is switch; must be null when action is current or initiate."),
    expectedConversationRevision: z.number().int().nonnegative().nullable().describe("Existing conversation only. Required when action is switch; must be null when action is current or initiate."),
    expectedManagerFence: z.number().int().nonnegative().nullable().describe("Existing conversation only. Required when action is switch; must be null when action is current or initiate."),
    reason: z.string().trim().min(5).max(1_000).nullable().describe("Internal explanation. Always allowed, including when action is current. Required when action is switch or initiate."),
}).strict().default({
    action: "current",
    targetId: null,
    conversationId: null,
    expectedConversationRevision: null,
    expectedManagerFence: null,
    reason: null,
});
export function selectedDeliveryTargetId(directive, currentConversationId) {
    if (directive.action === "initiate") {
        return directive.targetId ?? currentConversationId;
    }
    if (directive.action === "switch") {
        return directive.targetId ?? directive.conversationId ?? currentConversationId;
    }
    return currentConversationId;
}
export const DraftProposalObjectSchema = z.object({
    message: z.string().min(1).max(4_000),
    messageParts: DraftMessagePartsSchema,
    summary: z.string().min(1).max(600),
    outcomeKey: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/u).nullable().default(null),
    facts: z
        .array(z.object({
        key: z.string().min(1).max(120),
        value: z.string().min(1).max(1_000),
        sourceMessageId: z.string().max(200).nullable(),
    }))
        .max(30),
    coverage: z
        .array(z.object({
        question: z.string().min(1).max(500),
        status: z.enum(["answered", "asked", "missing", "not_applicable"]),
        evidence: z.string().max(1_000).nullable(),
    }))
        .max(30),
    policyFlags: z
        .array(z.object({
        code: z.string().min(1).max(120),
        severity: z.enum(["info", "warning", "block"]),
        message: z.string().min(1).max(600),
    }))
        .max(30),
    knowledgeCitations: z
        .array(z.object({
        chunkId: z.string().uuid(),
        claim: z.string().trim().min(1).max(600),
    }))
        .max(20),
    replyDirective: z.object({
        mode: z.enum(["reply", "no_reply"]),
        reason: z.string().trim().min(5).max(1_000).nullable().describe("Internal explanation of the reply decision. Required when mode is no_reply; optional when mode is reply."),
    }).strict().default({ mode: "reply", reason: null }),
    replyChannelDirective: ReplyChannelDirectiveSchema,
    followUpDirective: z.object({
        action: z.enum(["keep", "reschedule", "cancel"]),
        scheduleId: z.string().uuid().nullable().describe("Mutation target only. Required when action is reschedule or cancel; must be null when action is keep."),
        dueAt: z.string().datetime().nullable().describe("Mutation target only. Required when action is reschedule; must be null when action is keep or cancel."),
        reason: z.string().trim().min(5).max(1_000).nullable().describe("Internal explanation. Always allowed, including when action is keep. Required when action is reschedule or cancel."),
    }).strict().default({
        action: "keep",
        scheduleId: null,
        dueAt: null,
        reason: null,
    }),
    memoryDirective: z.array(z.object({
        key: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/u),
        kind: z.enum(["customer_fact", "relationship_summary", "scenario_progress"]),
        content: z.string().trim().min(3).max(1_000),
        sourceMessageId: z.string().uuid(),
        confidence: z.number().min(0.5).max(1),
    }).strict()).max(8).default([]),
    nextCheckDirective: z.object({
        action: z.enum(["none", "schedule"]),
        dueAt: z.string().datetime().nullable().describe("Scheduling field. Required when action is schedule; must be null when action is none."),
        intention: z.string().trim().min(5).max(600).nullable().describe("Scheduling field. Required when action is schedule; must be null when action is none."),
        reason: z.string().trim().min(5).max(600).nullable().describe("Internal explanation. Always allowed, including when action is none. Required when action is schedule."),
    }).strict().default({
        action: "none",
        dueAt: null,
        intention: null,
        reason: null,
    }),
    conversationKindDirective: z.object({
        action: z.enum(["none", "mark_service"]),
        reason: z.string().trim().min(5).max(1_000).nullable().describe("Internal explanation. Required when action is mark_service. VEHA never applies this without a separate operator confirmation, including in Autopilot."),
    }).strict().default({
        action: "none",
        reason: null,
    }),
    suggestedActions: z.array(SuggestedActionSchema).max(10),
    handoff: z.object({
        required: z.boolean().describe("Must be false when replyDirective.mode is no_reply: VEHA records those as separate outcomes."),
        reason: z.string().max(600).nullable(),
        question: z.string().trim().min(3).max(1_000).nullable().default(null),
        blocking: z.boolean().default(false),
        recommendedAction: z.string().trim().min(3).max(1_000).nullable().default(null),
        topic: z.enum(["owner_decision", "technical"]).default("owner_decision").describe("owner_decision: only Pasha can decide it (an exception to a rule, money, a promise, a policy gap). technical: a VEHA, calendar, CRM, delivery status, Mini App or site malfunction or missing evidence; VEHA routes it to engineering, never to Pasha."),
        // 21.10.2026 (23922347): five identical discount escalations in ten minutes.
        // The relation to an escalation already in ownerEscalations is Yana's
        // explicit choice; VEHA never guesses it from text similarity.
        relation: z.enum(["new", "same_question", "clarification"]).nullable().default(null).describe("Only when required is true, otherwise null. Compare with ownerEscalations of the same topic. same_question: that escalation already asks this (also a paraphrase); VEHA sends nothing new and it stays awaiting the answer. clarification: the customer added a substantive new fact to that question; VEHA appends the fact to it without a second notification. new: an independent question or a new technical symptom; VEHA creates a new escalation."),
        existingEscalationId: z.string().uuid().nullable().default(null).describe("The exact escalationId from ownerEscalations for same_question or clarification; null for new and when required is false."),
    }),
}).superRefine((proposal, context) => {
    const memoryKeys = new Set();
    proposal.memoryDirective.forEach((memory, index) => {
        if (memoryKeys.has(memory.key)) {
            context.addIssue({
                code: "custom",
                message: "A memory key can be updated only once per resolver decision.",
                path: ["memoryDirective", index, "key"],
            });
        }
        memoryKeys.add(memory.key);
    });
    if (proposal.replyDirective.mode === "no_reply") {
        if (proposal.replyDirective.reason === null) {
            context.addIssue({
                code: "custom",
                message: "A no-reply decision requires a reason.",
                path: ["replyDirective", "reason"],
            });
        }
        if (proposal.suggestedActions.some((action) => ![
            "none", "crm_note", "crm_stage", "crm_change_responsible",
        ].includes(action.type))) {
            context.addIssue({
                code: "custom",
                message: "A no-reply decision can authorize CRM actions only.",
                path: ["suggestedActions"],
            });
        }
        if (proposal.followUpDirective.action !== "keep") {
            context.addIssue({
                code: "custom",
                message: "A no-reply decision cannot mutate a follow-up without review.",
                path: ["followUpDirective", "action"],
            });
        }
        if (proposal.handoff.required) {
            context.addIssue({
                code: "custom",
                message: "A no-reply decision and a handoff are separate outcomes.",
                path: ["handoff", "required"],
            });
        }
        if (proposal.replyChannelDirective.action !== "current") {
            context.addIssue({
                code: "custom",
                message: "A no-reply decision cannot switch the delivery channel.",
                path: ["replyChannelDirective", "action"],
            });
        }
    }
    if (proposal.conversationKindDirective.action === "mark_service") {
        if (proposal.suggestedActions.some((action) => action.type !== "none")) {
            context.addIssue({
                code: "custom",
                message: "A service-conversation proposal requires separate confirmation and cannot execute CRM actions.",
                path: ["suggestedActions"],
            });
        }
        if (proposal.replyDirective.mode !== "no_reply") {
            context.addIssue({
                code: "custom",
                message: "A service-conversation proposal is only valid on a no-reply package.",
                path: ["conversationKindDirective", "action"],
            });
        }
        if (proposal.conversationKindDirective.reason === null) {
            context.addIssue({
                code: "custom",
                message: "A service-conversation proposal requires a reason.",
                path: ["conversationKindDirective", "reason"],
            });
        }
    }
    const replyChannel = proposal.replyChannelDirective;
    if (replyChannel.action === "current") {
        if (replyChannel.targetId !== null
            || replyChannel.conversationId !== null
            || replyChannel.expectedConversationRevision !== null
            || replyChannel.expectedManagerFence !== null) {
            context.addIssue({
                code: "custom",
                message: "The current reply channel cannot carry switch or initiate target fields.",
                path: ["replyChannelDirective"],
            });
        }
    }
    else if (replyChannel.action === "switch") {
        if (replyChannel.conversationId === null
            || replyChannel.expectedConversationRevision === null
            || replyChannel.expectedManagerFence === null
            || replyChannel.reason === null) {
            context.addIssue({
                code: "custom",
                message: "A channel switch requires an exact conversation, fences and reason.",
                path: ["replyChannelDirective"],
            });
        }
    }
    else if (replyChannel.targetId === null
        || replyChannel.conversationId !== null
        || replyChannel.expectedConversationRevision !== null
        || replyChannel.expectedManagerFence !== null
        || replyChannel.reason === null) {
        context.addIssue({
            code: "custom",
            message: "A channel initiation requires an exact supplied target id and reason, without conversation fences.",
            path: ["replyChannelDirective"],
        });
    }
    const followUp = proposal.followUpDirective;
    if (followUp.action === "keep") {
        if (followUp.scheduleId !== null || followUp.dueAt !== null) {
            context.addIssue({
                code: "custom",
                message: "A keep follow-up directive cannot carry mutation target fields.",
                path: ["followUpDirective"],
            });
        }
    }
    else {
        if (followUp.scheduleId === null || followUp.reason === null) {
            context.addIssue({
                code: "custom",
                message: "A follow-up mutation requires an exact schedule id and reason.",
                path: ["followUpDirective"],
            });
        }
        if (followUp.action === "reschedule" && followUp.dueAt === null) {
            context.addIssue({
                code: "custom",
                message: "A follow-up reschedule requires a new due time.",
                path: ["followUpDirective", "dueAt"],
            });
        }
        if (followUp.action === "cancel" && followUp.dueAt !== null) {
            context.addIssue({
                code: "custom",
                message: "A follow-up cancellation cannot carry a due time.",
                path: ["followUpDirective", "dueAt"],
            });
        }
    }
    const nextCheck = proposal.nextCheckDirective;
    if (nextCheck.action === "none") {
        if (nextCheck.dueAt !== null || nextCheck.intention !== null) {
            context.addIssue({
                code: "custom",
                message: "An empty next check cannot carry scheduling fields.",
                path: ["nextCheckDirective"],
            });
        }
    }
    else if (nextCheck.dueAt === null
        || nextCheck.intention === null
        || nextCheck.reason === null) {
        context.addIssue({
            code: "custom",
            message: "A proactive next check requires time, intention and reason.",
            path: ["nextCheckDirective"],
        });
    }
    if (proposal.handoff.required) {
        if (proposal.handoff.reason === null) {
            context.addIssue({
                code: "custom",
                message: "A handoff requires a reason.",
                path: ["handoff"],
            });
        }
        if ((proposal.handoff.relation === "same_question" || proposal.handoff.relation === "clarification")
            !== (proposal.handoff.existingEscalationId !== null)) {
            context.addIssue({
                code: "custom",
                message: "same_question and clarification name one existingEscalationId; new and an unset relation name none.",
                path: ["handoff", "existingEscalationId"],
            });
        }
    }
    else if (proposal.handoff.reason !== null
        || proposal.handoff.question !== null
        || proposal.handoff.recommendedAction !== null
        || proposal.handoff.relation !== null
        || proposal.handoff.existingEscalationId !== null) {
        context.addIssue({
            code: "custom",
            message: "A non-handoff proposal cannot carry escalation details.",
            path: ["handoff"],
        });
    }
});
function proposalWithMessageParts(input) {
    if (typeof input !== "object" || input === null || Array.isArray(input))
        return input;
    const suggestedActions = Array.isArray(Reflect.get(input, "suggestedActions"))
        ? Reflect.get(input, "suggestedActions").map((candidate) => {
            if (typeof candidate !== "object" || candidate === null || Array.isArray(candidate)) {
                return candidate;
            }
            const action = candidate;
            if (action.type === "send_media" && typeof action.caption === "string") {
                return {
                    ...action,
                    caption: normalizeCustomerFacingPunctuation(action.caption),
                };
            }
            if (action.type === "reminder") {
                return {
                    ...action,
                    ...(typeof action.text === "string"
                        ? { text: normalizeCustomerFacingPunctuation(action.text) }
                        : {}),
                    ...(typeof action.message === "string"
                        ? { message: normalizeCustomerFacingPunctuation(action.message) }
                        : {}),
                };
            }
            return action;
        })
        : Reflect.get(input, "suggestedActions");
    if (Object.hasOwn(input, "messageParts")) {
        const messageParts = Reflect.get(input, "messageParts");
        if (!Array.isArray(messageParts) || typeof messageParts[0] !== "string")
            return input;
        const normalizedParts = messageParts.map((part) => (typeof part === "string" ? normalizeCustomerFacingPunctuation(part) : part));
        return {
            ...input,
            message: normalizedParts[0],
            messageParts: normalizedParts,
            suggestedActions,
        };
    }
    const message = Reflect.get(input, "message");
    if (typeof message !== "string")
        return input;
    const normalizedMessage = normalizeCustomerFacingPunctuation(message);
    return {
        ...input,
        message: normalizedMessage,
        messageParts: [normalizedMessage],
        suggestedActions,
    };
}
export const DraftProposalSchema = z.preprocess(proposalWithMessageParts, DraftProposalObjectSchema).superRefine((proposal, context) => {
    // A provider action is not permission to send a blank bubble. Validate new
    // decisions without making historical persisted drafts unreadable.
    if (proposal.replyDirective.mode !== "reply")
        return;
    proposal.messageParts.forEach((part, index) => {
        if (part.trim().length === 0) {
            context.addIssue({
                code: "custom",
                message: "A customer reply must contain non-whitespace text.",
                path: ["messageParts", index],
            });
        }
    });
});
export function draftProposalContent(proposal) {
    return JSON.stringify(proposal.messageParts);
}
export const DraftPolicyReasonCodeSchema = z.enum([
    "MODEL_POLICY_BLOCK",
    "PROHIBITED_EXACT_PHRASE",
    "PROHIBITED_NORMALIZED_PHRASE",
    "ACTION_NOT_ALLOWED",
    "KNOWLEDGE_CITATION_REQUIRED",
    "KNOWLEDGE_CLARIFICATION_REQUIRED",
    "CONVERSATION_REVISION_STALE",
    "MANAGER_FENCE_STALE",
    "DRAFT_REVISION_STALE",
    "HANDOFF_REQUIRED",
]);
export const DraftPolicyReasonSchema = z.object({
    code: DraftPolicyReasonCodeSchema,
    reference: z.string().min(1).max(600),
    message: z.string().min(1).max(1_000),
}).strict();
export const DraftPolicyEvaluationSchema = z.object({
    allowed: z.boolean(),
    requiresHandoff: z.boolean(),
    reasons: z.array(DraftPolicyReasonSchema).max(100),
    // Model block flags a no-reply decision satisfies by sending nothing.
    waivedModelBlocks: z.array(z.object({
        code: z.string().min(1).max(120),
        message: z.string().min(1).max(600),
    }).strict()).max(30).optional(),
}).strict();
export const ExcludedDraftActionSchema = z.object({
    type: ScenarioActionTypeSchema,
    reason: z.string().trim().min(1).max(600),
}).strict();
export const ContactDeliveryPlanSchema = z.object({
    targetId: z.string().uuid(),
    // The first seven are the shared contact rule (27.09.2026); the rest remain
    // readable in drafts planned before it.
    reason: z.enum(["customer_replied", "delivered", "telegram_chat", "open_telegram_id", "open_telegram_username",
        "open_telegram_phone", "whatsapp_6560", "whatsapp_2746", "whatsapp_4599",
        "manager_channel", "try_whatsapp_after_telegram", "whatsapp_recovered", "telegram_first", "available_channel"]),
}).strict();
export const PersistedDraftProposalSchema = z.preprocess((input) => {
    if (typeof input !== "object" || input === null || Array.isArray(input))
        return input;
    const withCitations = Object.hasOwn(input, "knowledgeCitations")
        ? input
        : { ...input, knowledgeCitations: [] };
    return proposalWithMessageParts(withCitations);
}, DraftProposalObjectSchema.extend({
    policyEvaluation: DraftPolicyEvaluationSchema.optional(),
    excludedActions: z.array(ExcludedDraftActionSchema).max(10).default([]),
    contactDeliveryPlan: ContactDeliveryPlanSchema.optional(),
}));
export const HistoricalCaseImportMessageSchema = z.object({
    direction: z.enum(["inbound", "outbound"]),
    actor: z.enum(["customer", "manager", "ai"]),
    text: z.string().min(1).max(4_000),
    occurredAt: z.string().datetime(),
}).strict().superRefine((message, context) => {
    if (message.direction === "inbound" && message.actor !== "customer") {
        context.addIssue({
            code: "custom",
            path: ["actor"],
            message: "An imported inbound message must belong to the customer.",
        });
    }
    if (message.direction === "outbound" && message.actor === "customer") {
        context.addIssue({
            code: "custom",
            path: ["actor"],
            message: "An imported outbound message cannot belong to the customer.",
        });
    }
});
export const HistoricalCaseImportItemSchema = z.object({
    semanticExpectation: SemanticExpectationSchema.optional(),
    clientKey: z.string().regex(/^[a-zA-Z0-9][a-zA-Z0-9_.:-]{0,119}$/u),
    title: z.string().trim().min(2).max(200),
    sourceKind: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/u),
    sourceRef: z.string().trim().min(1).max(500),
    sourceStageLabel: z.string().trim().min(1).max(200).optional(),
    expectedBehavior: z.string().trim().min(2).max(1_000).optional(),
    expectedResponse: z.string().min(1).max(4_000).optional(),
    messages: z.array(HistoricalCaseImportMessageSchema).min(1).max(60),
}).strict().superRefine((item, context) => {
    if (!item.messages.some((message) => (message.direction === "inbound" && message.actor === "customer"))) {
        context.addIssue({
            code: "custom",
            path: ["messages"],
            message: "An imported historical case requires a customer message.",
        });
    }
    const last = item.messages.at(-1);
    if (last?.direction !== "inbound" || last.actor !== "customer") {
        context.addIssue({
            code: "custom",
            path: ["messages", Math.max(0, item.messages.length - 1)],
            message: "An imported historical case must end on the customer message being evaluated.",
        });
    }
    for (let index = 1; index < item.messages.length; index += 1) {
        if (Date.parse(item.messages[index].occurredAt) < Date.parse(item.messages[index - 1].occurredAt)) {
            context.addIssue({
                code: "custom",
                path: ["messages", index, "occurredAt"],
                message: "Imported historical messages must be chronological.",
            });
        }
    }
});
export const HistoricalCaseImportMutationSchema = z.object({
    schemaVersion: z.literal("historical_cases_v1"),
    cases: z.array(HistoricalCaseImportItemSchema).min(1).max(20),
    reason: z.string().trim().min(5).max(1_000),
    idempotencyKey: z.string().trim().min(16).max(160),
}).strict().superRefine((mutation, context) => {
    const keys = mutation.cases.map((item) => item.clientKey);
    if (new Set(keys).size !== keys.length) {
        context.addIssue({
            code: "custom",
            path: ["cases"],
            message: "Each imported historical case requires a unique client key.",
        });
    }
});
export const HISTORICAL_CASE_IMPORT_MAX_BYTES = 6 * 1024 * 1024;
export const HistoricalCaseImportResultSchema = z.object({
    importId: z.string().uuid(),
    status: z.enum(["imported", "idempotent_replay"]),
    replayed: z.boolean(),
    cases: z.array(z.object({
        clientKey: z.string().min(1).max(120),
        historicalCaseId: z.string().uuid(),
        created: z.boolean(),
    }).strict()).min(1).max(20),
}).strict();
export const HistoricalCaseSummarySchema = z.object({
    id: z.string().uuid(),
    title: z.string().trim().min(2).max(200),
    sourceKind: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/u),
    sourceRef: z.string().trim().min(1).max(500),
    sourceStageLabel: z.string().trim().min(1).max(200).nullable(),
    expectedBehavior: z.string().trim().min(2).max(1_000).nullable(),
    expectedResponse: z.string().min(1).max(4_000).nullable(),
    messageCount: z.number().int().min(1).max(60),
    lastCustomerMessage: z.string().max(4_000),
    createdAt: z.string().datetime(),
}).strict();
export const HistoricalCaseSummariesSchema = z.array(HistoricalCaseSummarySchema).max(100);
export const HistoricalReplayItemInputSchema = z.object({
    conversationId: z.string().uuid().optional(),
    historicalCaseId: z.string().uuid().optional(),
    stageNodeId: z.string().uuid(),
    inputMessageId: z.string().uuid().optional(),
    inputConversationRevision: z.number().int().positive().optional(),
    inputHash: z.string().regex(/^[a-f0-9]{64}$/u).optional(),
}).strict().superRefine((item, context) => {
    if ((item.conversationId === undefined) === (item.historicalCaseId === undefined)) {
        context.addIssue({
            code: "custom",
            path: [item.conversationId === undefined ? "conversationId" : "historicalCaseId"],
            message: "A replay case must reference exactly one conversation or imported historical case.",
        });
    }
    if (item.historicalCaseId !== undefined && (item.inputMessageId !== undefined
        || item.inputConversationRevision !== undefined
        || item.inputHash !== undefined)) {
        context.addIssue({
            code: "custom",
            path: ["historicalCaseId"],
            message: "An imported historical case already owns its immutable input snapshot.",
        });
    }
    if ((item.inputMessageId === undefined) !== (item.inputConversationRevision === undefined)) {
        context.addIssue({
            code: "custom",
            path: [item.inputMessageId === undefined ? "inputMessageId" : "inputConversationRevision"],
            message: "Pinned replay message id and conversation revision must be supplied together.",
        });
    }
});
export const HistoricalReplayInputMessageSnapshotSchema = z.object({
    id: z.string().uuid(),
    revision: z.number().int().positive(),
    direction: z.enum(["inbound", "outbound"]),
    actor: z.enum(["customer", "manager", "ai"]),
    text: z.string().max(4_000),
    occurredAt: z.string().datetime(),
}).strict();
export const HistoricalReplayInputSnapshotSchema = z.array(HistoricalReplayInputMessageSnapshotSchema).min(1).max(60);
const HistoricalReplayScheduleSlotSchema = z.object({
    localTime: z.string().regex(/^\d{4}-\d{2}-\d{2}T(?:[01]\d|2[0-3]):[0-5]\d$/u),
    label: z.string().trim().min(1).max(200),
}).strict();
export const HistoricalReplayScheduleBindingSnapshotSchema = z.object({
    bindingKey: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/u),
    displayName: z.string().trim().min(1).max(200),
    timezone: z.string().trim().min(1).max(100),
    slotDurationMinutes: z.number().int().min(5).max(1_440),
    availableSlots: z.array(HistoricalReplayScheduleSlotSchema).max(40).optional(),
    managers: z.array(z.object({
        manager: z.string().trim().min(1).max(200),
        managerLabel: z.string().trim().min(1).max(200),
        monthlyConfirmedLoad: z.number().int().min(0).max(10_000),
        availableSlots: z.array(HistoricalReplayScheduleSlotSchema).max(40),
    }).strict()).max(100).optional(),
}).strict().superRefine((binding, context) => {
    const hasLegacySlots = (binding.availableSlots?.length ?? 0) > 0;
    const hasManagers = (binding.managers?.length ?? 0) > 0;
    if (!hasLegacySlots && !hasManagers) {
        context.addIssue({
            code: "custom",
            path: ["managers"],
            message: "A replay schedule binding must expose managers or legacy availableSlots.",
        });
    }
});
export const HistoricalReplayScheduleSnapshotSchema = z.object({
    source: z.literal("active_published_schedule").optional(),
    capturedAt: z.string().datetime().optional(),
    bindings: z.array(HistoricalReplayScheduleBindingSnapshotSchema).min(1).max(10),
    reschedule: z.object({
        contextKind: z.literal("simulated_current_booking").optional(),
        bookingRef: z.string().uuid(),
        bindingKey: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/u),
        currentLocalTime: z.string().regex(/^\d{4}-\d{2}-\d{2}T(?:[01]\d|2[0-3]):00$/u),
        currentLabel: z.string().trim().min(1).max(200),
        remainingAttempts: z.number().int().min(0).max(1),
    }).strict().nullable().optional(),
}).strict().superRefine((schedule, context) => {
    const bindingKeys = new Set(schedule.bindings.map((binding) => binding.bindingKey));
    if (bindingKeys.size !== schedule.bindings.length) {
        context.addIssue({
            code: "custom",
            path: ["bindings"],
            message: "A replay schedule binding key can appear only once.",
        });
    }
    if (schedule.reschedule && !bindingKeys.has(schedule.reschedule.bindingKey)) {
        context.addIssue({
            code: "custom",
            path: ["reschedule", "bindingKey"],
            message: "The current replay booking must reference a supplied schedule binding.",
        });
    }
});
export const HistoricalReplayScheduleSnapshotRequestSchema = z.object({
    scenarioVersionId: z.string().uuid(),
    stageNodeIds: z.array(z.string().uuid()).min(1).max(20),
}).strict().superRefine((request, context) => {
    if (new Set(request.stageNodeIds).size !== request.stageNodeIds.length) {
        context.addIssue({
            code: "custom",
            path: ["stageNodeIds"],
            message: "A stage node can be selected only once for a replay schedule snapshot.",
        });
    }
});
export const HistoricalReplayScheduleSnapshotResultSchema = z.object({
    schemaVersion: z.literal("historical_replay_schedule_snapshot_v1"),
    scenarioVersionId: z.string().uuid(),
    capturedAt: z.string().datetime(),
    requiredActionTypes: z.array(z.enum(["booking", "booking_cancel"])).max(2),
    schedule: HistoricalReplayScheduleSnapshotSchema.nullable(),
}).strict().superRefine((result, context) => {
    if (new Set(result.requiredActionTypes).size !== result.requiredActionTypes.length) {
        context.addIssue({
            code: "custom",
            path: ["requiredActionTypes"],
            message: "A required replay schedule action can appear only once.",
        });
    }
    if (result.requiredActionTypes.length === 0 && result.schedule !== null) {
        context.addIssue({
            code: "custom",
            path: ["schedule"],
            message: "A replay schedule snapshot is allowed only for selected schedule actions.",
        });
    }
    if (result.requiredActionTypes.length > 0) {
        if (result.schedule === null
            || result.schedule.source !== "active_published_schedule"
            || result.schedule.capturedAt !== result.capturedAt) {
            context.addIssue({
                code: "custom",
                path: ["schedule"],
                message: "Required replay schedule actions need one current published provider snapshot.",
            });
        }
    }
    if (result.requiredActionTypes.includes("booking_cancel")
        && result.schedule?.reschedule?.contextKind !== "simulated_current_booking") {
        context.addIssue({
            code: "custom",
            path: ["schedule", "reschedule"],
            message: "A replay cancellation needs an explicitly simulated current booking context.",
        });
    }
});
const HistoricalReplayMediaSnapshotSchema = z.object({
    assets: z.array(z.object({
        assetId: z.string().uuid(),
        title: z.string().trim().min(2).max(200),
        mediaKind: CompanyMediaKindSchema,
        fileName: z.string().trim().min(1).max(240),
    }).strict()).min(1).max(100),
}).strict().superRefine((media, context) => {
    if (new Set(media.assets.map((asset) => asset.assetId)).size !== media.assets.length) {
        context.addIssue({
            code: "custom",
            path: ["assets"],
            message: "A replay media asset can appear only once.",
        });
    }
});
export const HistoricalReplayToolSnapshotSchema = z.object({
    schemaVersion: z.literal("historical_replay_tools_v1"),
    schedule: HistoricalReplayScheduleSnapshotSchema.optional(),
    media: HistoricalReplayMediaSnapshotSchema.optional(),
}).strict().superRefine((snapshot, context) => {
    if (snapshot.schedule === undefined && snapshot.media === undefined) {
        context.addIssue({
            code: "custom",
            message: "A replay tool snapshot must supply schedule or media evidence.",
        });
    }
});
export const HistoricalReplayStartMutationSchema = z.object({
    scenarioVersionId: z.string().uuid(),
    cases: z.array(HistoricalReplayItemInputSchema).min(5).max(20),
    toolSnapshot: HistoricalReplayToolSnapshotSchema.optional(),
    reason: z.string().trim().min(5).max(1_000),
    idempotencyKey: z.string().trim().min(16).max(160),
}).strict().superRefine((mutation, context) => {
    const identities = mutation.cases.map((item) => (item.conversationId ?? item.historicalCaseId));
    if (new Set(identities).size !== identities.length) {
        context.addIssue({
            code: "custom",
            path: ["cases"],
            message: "Each historical replay input can be selected only once.",
        });
    }
});
export const HistoricalReplayStartResultSchema = z.object({
    runId: z.string().uuid(),
    status: z.enum(["queued", "idempotent_replay"]),
    replayed: z.boolean(),
}).strict();
export const HistoricalReplayItemStatusSchema = z.enum([
    "queued",
    "running",
    "completed",
    "failed",
]);
const HistoricalReplayActionResultBaseShape = {
    actionIndex: z.number().int().min(1).max(10),
    actionHash: z.string().regex(/^[a-f0-9]{64}$/u),
};
export const HistoricalReplayActionResultSchema = z.discriminatedUnion("type", [
    z.object({
        ...HistoricalReplayActionResultBaseShape,
        type: z.literal("none"),
        status: z.literal("skipped"),
        reason: z.string().min(1).max(500),
    }).strict(),
    z.object({
        ...HistoricalReplayActionResultBaseShape,
        type: z.enum([
            "crm_note",
            "crm_stage",
            "crm_task",
            "crm_set_fields",
            "crm_change_responsible",
            "table_upsert",
            "reminder",
        ]),
        status: z.literal("simulated"),
    }).strict(),
    z.object({
        ...HistoricalReplayActionResultBaseShape,
        type: z.literal("booking"),
        status: z.literal("simulated"),
        bindingKey: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/u),
        localTime: z.string().regex(/^\d{4}-\d{2}-\d{2}T(?:[01]\d|2[0-3]):[0-5]\d$/u),
        label: z.string().trim().min(1).max(200),
        simulatedBookingRef: z.string().uuid(),
    }).strict(),
    z.object({
        ...HistoricalReplayActionResultBaseShape,
        type: z.literal("booking_cancel"),
        status: z.literal("simulated"),
        bookingRef: z.string().uuid(),
    }).strict(),
    z.object({
        ...HistoricalReplayActionResultBaseShape,
        type: z.literal("send_media"),
        status: z.literal("simulated"),
        assetId: z.string().uuid(),
        title: z.string().trim().min(2).max(200),
        mediaKind: CompanyMediaKindSchema,
        fileName: z.string().trim().min(1).max(240),
    }).strict(),
]);
export const HistoricalReplayItemSchema = z.object({
    semanticExpectation: SemanticExpectationSchema.nullable().default(null),
    runtimeVersion: z.string().nullable().default(null),
    knowledgeVersionIds: z.array(z.string().uuid()).default([]),
    stagingRuntimeHash: z.string().regex(/^[a-f0-9]{64}$/u).nullable().default(null),
    usage: EvaluationUsageSchema.default(emptyEvaluationUsage),
    id: z.string().uuid(),
    conversationId: z.string().uuid().nullable(),
    historicalCaseId: z.string().uuid().nullable(),
    historicalCaseTitle: z.string().trim().min(2).max(200).nullable(),
    referenceResponse: z.string().min(1).max(4_000).nullable(),
    expectedBehavior: z.string().trim().min(2).max(1_000).nullable(),
    stageNodeId: z.string().uuid(),
    stageTitle: z.string().trim().min(1).max(120),
    employeeVersionId: z.string().uuid(),
    inputMessageId: z.string().uuid(),
    inputConversationRevision: z.number().int().positive(),
    inputHash: z.string().regex(/^[a-f0-9]{64}$/u),
    status: HistoricalReplayItemStatusSchema,
    proposal: DraftProposalSchema.nullable(),
    outcomeKey: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/u).nullable(),
    transitionDisposition: z.enum(["stay", "node", "handoff", "terminal"]).nullable(),
    transitionTargetStageNodeId: z.string().uuid().nullable(),
    actionResults: z.array(HistoricalReplayActionResultSchema).max(10),
    policyEvaluation: DraftPolicyEvaluationSchema.nullable(),
    model: z.string().trim().min(1).max(200).nullable(),
    runtimeAdapterKind: z.string().trim().min(1).max(100).nullable(),
    errorCode: z.string().trim().min(1).max(120).nullable(),
    errorMessage: z.string().trim().min(1).max(1_000).nullable(),
    completedAt: z.string().datetime().nullable(),
}).strict().superRefine((item, context) => {
    if ((item.conversationId === null) === (item.historicalCaseId === null)) {
        context.addIssue({
            code: "custom",
            path: ["historicalCaseId"],
            message: "A replay item must expose exactly one input source.",
        });
    }
    if (item.historicalCaseId === null && (item.historicalCaseTitle !== null
        || item.referenceResponse !== null
        || item.expectedBehavior !== null)) {
        context.addIssue({
            code: "custom",
            path: ["historicalCaseTitle"],
            message: "Historical reference evidence requires an imported case.",
        });
    }
    const targetRequired = item.transitionDisposition === "stay"
        || item.transitionDisposition === "node";
    if (targetRequired !== (item.transitionTargetStageNodeId !== null)) {
        context.addIssue({
            code: "custom",
            path: ["transitionTargetStageNodeId"],
            message: targetRequired
                ? "A stay or node replay transition requires its exact target node."
                : "A replay target node is allowed only for a stay or node transition.",
        });
    }
});
export const HistoricalReplayRunStatusSchema = z.enum([
    "queued",
    "running",
    "completed",
    "completed_with_failures",
    "failed",
]);
export const HistoricalReplayRunSchema = z.object({
    usage: EvaluationUsageSchema.default(emptyEvaluationUsage),
    id: z.string().uuid(),
    scenarioId: z.string().uuid(),
    scenarioVersionId: z.string().uuid(),
    scenarioVersion: z.number().int().positive(),
    scenarioName: z.string().trim().min(1).max(200),
    toolSnapshot: HistoricalReplayToolSnapshotSchema.nullable(),
    toolSnapshotHash: z.string().regex(/^[a-f0-9]{64}$/u).nullable(),
    status: HistoricalReplayRunStatusSchema,
    reason: z.string().trim().min(5).max(1_000),
    itemCount: z.number().int().min(5).max(20),
    completedCount: z.number().int().nonnegative(),
    failedCount: z.number().int().nonnegative(),
    createdAt: z.string().datetime(),
    completedAt: z.string().datetime().nullable(),
    items: z.array(HistoricalReplayItemSchema).max(20),
}).strict().superRefine((run, context) => {
    if ((run.toolSnapshot === null) !== (run.toolSnapshotHash === null)) {
        context.addIssue({
            code: "custom",
            path: [run.toolSnapshot === null ? "toolSnapshotHash" : "toolSnapshot"],
            message: "Replay tool snapshot and hash must be returned together.",
        });
    }
});
export const HistoricalReplayRunsSchema = z.array(HistoricalReplayRunSchema).max(50);
export const ScenarioSimulationSessionStartMutationSchema = z.object({
    toolSnapshot: HistoricalReplayToolSnapshotSchema.optional(),
    scenarioVersionId: z.string().uuid(),
    startStageNodeId: z.string().uuid(),
    title: z.string().trim().min(2).max(200),
    referenceDialogueKey: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/u).optional(),
    idempotencyKey: z.string().trim().min(16).max(160),
}).strict();
const ScenarioSimulationCustomerMessageSchema = z.string().min(1).max(4_000).superRefine((message, context) => {
    if (message !== message.trim()) {
        context.addIssue({
            code: "custom",
            message: "A simulated customer message must not contain outer whitespace.",
        });
    }
});
export const ScenarioSimulationCustomerMessagesSchema = z.array(ScenarioSimulationCustomerMessageSchema).min(1).max(20).superRefine((messages, context) => {
    const joinedLength = messages.reduce((total, message) => total + message.length, 0)
        + messages.length - 1;
    if (joinedLength > 8_000) {
        context.addIssue({
            code: "custom",
            message: "A simulated customer message batch is limited to 8000 characters.",
        });
    }
});
export const ScenarioSimulationTurnMutationSchema = z.object({
    customerMessage: ScenarioSimulationCustomerMessageSchema.optional(),
    customerMessages: ScenarioSimulationCustomerMessagesSchema.optional(),
    idempotencyKey: z.string().trim().min(16).max(160),
}).strict().superRefine((mutation, context) => {
    if ((mutation.customerMessage === undefined) === (mutation.customerMessages === undefined)) {
        context.addIssue({
            code: "custom",
            path: ["customerMessages"],
            message: "Provide exactly one of customerMessage or customerMessages.",
        });
    }
});
export const ScenarioSimulationMutationResultSchema = z.object({
    sessionId: z.string().uuid(),
    turnId: z.string().uuid().nullable(),
    status: z.enum(["created", "queued", "idempotent_replay"]),
    replayed: z.boolean(),
}).strict();
const ScenarioSimulationActionResultBaseShape = {
    actionIndex: z.number().int().min(1).max(10),
    status: z.literal("simulated"),
};
export const ScenarioSimulationActionResultSchema = z.discriminatedUnion("type", [
    z.object({
        ...ScenarioSimulationActionResultBaseShape,
        type: z.literal("send_media"),
        assetId: z.string().uuid(),
        title: z.string().trim().min(2).max(200),
        mediaKind: CompanyMediaKindSchema,
        fileName: z.string().trim().min(1).max(240),
    }).strict(),
    z.object({
        ...ScenarioSimulationActionResultBaseShape,
        type: z.literal("reminder"),
        dueAt: z.string().datetime(),
        message: z.string().trim().min(1).max(4_000),
    }).strict(),
]);
export const ScenarioSimulationTurnSchema = z.object({
    allActionResults: z.array(HistoricalReplayActionResultSchema).max(10).default([]),
    usage: EvaluationUsageSchema.default(emptyEvaluationUsage),
    id: z.string().uuid(),
    sequenceNumber: z.number().int().positive().max(100),
    customerMessage: z.string().trim().min(1).max(8_000),
    customerMessages: ScenarioSimulationCustomerMessagesSchema,
    status: z.enum(["queued", "running", "completed", "failed"]),
    inputStageNodeId: z.string().uuid(),
    inputStageTitle: z.string().trim().min(1).max(120),
    outputStageNodeId: z.string().uuid().nullable(),
    outputStageTitle: z.string().trim().min(1).max(120).nullable(),
    outcomeKey: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/u).nullable(),
    transitionDisposition: z.enum(["stay", "node", "handoff", "terminal"]).nullable(),
    proposal: DraftProposalSchema.nullable(),
    actionResults: z.array(ScenarioSimulationActionResultSchema).max(10).default([]),
    policyEvaluation: DraftPolicyEvaluationSchema.nullable(),
    model: z.string().trim().min(1).max(200).nullable(),
    runtimeAdapterKind: z.string().trim().min(1).max(100).nullable(),
    errorCode: z.string().trim().min(1).max(120).nullable(),
    errorMessage: z.string().trim().min(1).max(1_000).nullable(),
    createdAt: z.string().datetime(),
    completedAt: z.string().datetime().nullable(),
}).strict();
export const ScenarioSimulationSessionSchema = z.object({
    usage: EvaluationUsageSchema.default(emptyEvaluationUsage),
    id: z.string().uuid(),
    scenarioId: z.string().uuid(),
    scenarioVersionId: z.string().uuid(),
    scenarioVersion: z.number().int().positive(),
    scenarioName: z.string().trim().min(1).max(200),
    startStageNodeId: z.string().uuid(),
    currentStageNodeId: z.string().uuid().nullable(),
    currentStageTitle: z.string().trim().min(1).max(120).nullable(),
    title: z.string().trim().min(2).max(200),
    referenceDialogueKey: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/u).nullable().optional(),
    status: z.enum(["active", "closed"]),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    turns: z.array(ScenarioSimulationTurnSchema).max(100),
}).strict();
export const ScenarioSimulationSessionsSchema = z.array(ScenarioSimulationSessionSchema).max(50);
export const ApiErrorSchema = z.object({
    error: z.object({
        code: z.string().min(1),
        message: z.string().min(1),
        requestId: z.string().min(1),
        details: z.record(z.string(), z.unknown()).optional(),
    }),
});
export const SessionActorSchema = z.object({
    userId: z.string().uuid(),
    organizationId: z.string().uuid(),
    email: z.string().email(),
    name: z.string().min(1).max(200),
    role: RoleSchema,
    sectionAccess: EffectiveSectionAccessSchema.nullable().optional(),
});
export const ReadinessCheckSchema = z.object({
    code: z.string().min(1).max(120),
    label: z.string().min(1).max(200),
    status: z.enum(["ready", "blocked", "warning", "not_configured"]),
    reason: z.string().min(1).max(600),
    nextAction: z.string().min(1).max(600),
});
export const DeliveryStateSchema = z.object({
    deliveryEnabled: z.boolean(),
    autoDeliveryEnabled: z.boolean(),
    mode: DeliveryModeSchema,
    inFlightCount: z.number().int().nonnegative(),
    readiness: z.array(ReadinessCheckSchema),
    updatedAt: z.string().datetime(),
});
export const DeliveryStateMutationSchema = z
    .object({
    deliveryEnabled: z.boolean(),
    autoDeliveryEnabled: z.boolean(),
    reason: z.string().trim().min(10).max(1_000),
    currentPassword: PasswordInputSchema,
    mfaCode: MfaAuthorizationCodeSchema.optional(),
})
    .superRefine((value, context) => {
    if (value.autoDeliveryEnabled && !value.deliveryEnabled) {
        context.addIssue({
            code: "custom",
            message: "Automatic delivery requires client delivery.",
            path: ["autoDeliveryEnabled"],
        });
    }
    if (value.autoDeliveryEnabled) {
        context.addIssue({
            code: "custom",
            message: "Autonomous delivery is not available in this release.",
            path: ["autoDeliveryEnabled"],
        });
    }
});
export const PrivilegedConfirmationSchema = z.object({
    currentPassword: PasswordInputSchema,
    mfaCode: MfaAuthorizationCodeSchema.optional(),
});
export const RestoreSafetyBlockerSchema = z.object({
    code: z.enum([
        "connector_not_ready",
        "connector_not_reverified",
        "dispatchable_effects_present",
    ]),
    connectorKind: ConnectorKindSchema.nullable(),
    label: z.string().min(1).max(200),
    reason: z.string().min(1).max(600),
});
export const RestoreSafetyStateSchema = z.object({
    holdActive: z.boolean(),
    operationId: z.string().uuid().nullable(),
    restoredAt: z.string().datetime().nullable(),
    ambiguousEffectCount: z.number().int().nonnegative(),
    blockers: z.array(RestoreSafetyBlockerSchema).max(100),
    updatedAt: z.string().datetime(),
});
export const RestoreReconciliationMutationSchema = z.object({
    operationId: z.string().uuid(),
    reason: z.string().trim().min(10).max(1_000),
    currentPassword: PasswordInputSchema,
    mfaCode: MfaAuthorizationCodeSchema,
});
export const ConnectorReadinessSchema = z.object({
    kind: ConnectorKindSchema,
    status: ConnectorStatusSchema,
    label: z.string().min(1).max(200),
    detail: z.string().min(1).max(600),
    checkedAt: z.string().datetime().nullable(),
});
export const AttentionItemSchema = z.object({
    id: z.string().uuid(),
    customerName: z.string().min(1).max(200),
    stageName: z.string().min(1).max(200),
    channel: z.string().min(1).max(100),
    reason: z.string().min(1).max(600),
    waitingMinutes: z.number().int().nonnegative(),
    draftStatus: z.enum(["not_requested", "generating", "ready", "blocked", "stale"]),
});
export const ActivationFirstPreviewSchema = z.object({
    conversationId: z.string().uuid(),
    draftRevisionId: z.string().uuid(),
    completedAt: z.string().datetime(),
}).strict();
export const ActivationStateSchema = z.object({
    firstPreview: ActivationFirstPreviewSchema.nullable(),
}).strict();
export const AppBootstrapSchema = z.object({
    source: z.literal("database"),
    scope: z.enum(["operator", "calendar"]).optional(),
    actor: SessionActorSchema.nullable(),
    installation: z.object({
        id: z.string().uuid(),
        name: z.string().min(1).max(200),
    }),
    activation: ActivationStateSchema,
    delivery: DeliveryStateSchema,
    connectors: z.array(ConnectorReadinessSchema),
    attention: z.array(AttentionItemSchema),
    metrics: z.object({
        waiting: z.number().int().nonnegative(),
        reviewReady: z.number().int().nonnegative(),
    }),
});
export const SetupStatusSchema = z.object({
    required: z.boolean(),
    expiresAt: z.string().datetime().nullable(),
});
export const SetupCompleteSchema = z.object({
    setupToken: z.string().min(32).max(512),
    email: z.string().trim().toLowerCase().email().max(320),
    displayName: z.string().trim().min(2).max(200),
    organizationName: z.string().trim().min(2).max(200),
    installationName: z.string().trim().min(2).max(200),
    password: NewPasswordSchema,
});
export const LoginSchema = z.object({
    email: z.string().trim().toLowerCase().email().max(320),
    password: PasswordInputSchema,
    /** «Запомнить меня»: no 12 h idle limit, the session lasts its 30 days. */
    remember: z.boolean().default(false),
});
export const SessionStateSchema = z.object({
    actor: SessionActorSchema,
    csrfToken: z.string().min(1),
    mfaEnrolled: z.boolean(),
});
export const AuthenticationStateSchema = z.object({
    setup: SetupStatusSchema,
    session: SessionStateSchema.nullable(),
}).superRefine((state, context) => {
    if (state.setup.required && state.session !== null) {
        context.addIssue({
            code: "custom",
            path: ["session"],
            message: "A session cannot be active while initial setup is required.",
        });
    }
});
export const MfaEnrollmentSchema = z.object({
    currentPassword: PasswordInputSchema,
    currentMfaCode: MfaAuthorizationCodeSchema.optional(),
});
export const MfaChallengeSchema = z.object({
    code: z.string().regex(/^\d{6}$/u),
});
export const WorkspaceMessageSchema = z.object({
    id: z.string().uuid(),
    author: z.enum(["customer", "manager", "ai"]),
    authorName: z.string().min(1).max(200),
    text: z.string().min(1).max(4_000),
    time: z.string().min(1).max(100),
});
export const ConversationNextActionSchema = z.object({
    kind: z.enum([
        "wait",
        "review_draft",
        "retry_crm_resolution",
        "retry_generation",
        "restore_missing_draft",
        "resume_after_manager_activity",
        "resume_after_policy_blocked_customer_reply",
        "connect_amocrm",
        "connect_openai",
        "configure_preview_scope",
        "configure_stage",
        "repair_scenario",
        "regenerate_policy_preview",
        "reply_manually",
        "return_to_yana",
        "none",
    ]),
    label: z.string().min(1).max(160),
    enabled: z.boolean(),
});
export const ConversationOperatorStateSchema = z.object({
    code: z.enum([
        "needs_response",
        "processing",
        "review_ready",
        "waiting_customer",
        "out_of_scope",
        "action_required",
        "closed",
    ]),
    reasonCode: z.string().regex(/^[A-Z0-9_]{1,120}$/u).nullable(),
    reason: z.string().min(1).max(600),
    nextAction: ConversationNextActionSchema,
});
export const LeadAuthorizationSchema = z.object({
    state: z.enum([
        "allowed",
        "not_allowed",
        "crm_lead_unresolved",
        "lead_binding_changed",
        "expired",
    ]),
    currentCrmLeadExternalId: z.string().min(1).max(500).nullable(),
    allowedCrmLeadExternalId: z.string().min(1).max(500).nullable(),
    allowlistId: z.string().uuid().nullable(),
    expiresAt: z.string().datetime().nullable(),
}).superRefine((authorization, context) => {
    if (authorization.state === "allowed") {
        if (!authorization.currentCrmLeadExternalId
            || !authorization.allowedCrmLeadExternalId
            || !authorization.allowlistId
            || authorization.currentCrmLeadExternalId !== authorization.allowedCrmLeadExternalId) {
            context.addIssue({
                code: "custom",
                message: "Allowed delivery requires one exact CRM lead and an exact allowlist grant.",
                path: ["state"],
            });
        }
    }
    if (authorization.state === "not_allowed" && authorization.allowlistId !== null) {
        context.addIssue({
            code: "custom",
            message: "A not-allowed conversation must not expose an active grant.",
            path: ["allowlistId"],
        });
    }
    if (authorization.state === "crm_lead_unresolved"
        && authorization.currentCrmLeadExternalId !== null) {
        context.addIssue({
            code: "custom",
            message: "An unresolved CRM lead must not expose a current lead id.",
            path: ["currentCrmLeadExternalId"],
        });
    }
    if (authorization.state === "lead_binding_changed" && authorization.allowlistId === null) {
        context.addIssue({
            code: "custom",
            message: "A changed lead binding requires the exact stale grant for explicit revocation.",
            path: ["allowlistId"],
        });
    }
    if (authorization.state === "expired"
        && (authorization.allowlistId === null || authorization.expiresAt === null)) {
        context.addIssue({
            code: "custom",
            message: "An expired authorization requires the exact grant and its expiration time.",
            path: [authorization.allowlistId === null ? "allowlistId" : "expiresAt"],
        });
    }
});
export const PreviewProcessingAuthorizationSchema = z.object({
    state: z.enum([
        "allowed",
        "not_allowed",
        "crm_lead_unresolved",
        "context_changed",
        "expired",
    ]),
    currentCrmLeadExternalId: z.string().min(1).max(500).nullable(),
    grantedCrmLeadExternalId: z.string().min(1).max(500).nullable(),
    grantId: z.string().uuid().nullable(),
    expiresAt: z.string().datetime().nullable(),
}).superRefine((authorization, context) => {
    if (authorization.state === "allowed") {
        if (!authorization.currentCrmLeadExternalId
            || !authorization.grantedCrmLeadExternalId
            || !authorization.grantId
            || authorization.currentCrmLeadExternalId !== authorization.grantedCrmLeadExternalId) {
            context.addIssue({
                code: "custom",
                message: "Allowed preview processing requires one exact CRM lead and an exact processing grant.",
                path: ["state"],
            });
        }
    }
    if (authorization.state === "not_allowed" && authorization.grantId !== null) {
        context.addIssue({
            code: "custom",
            message: "A not-allowed conversation must not expose an active processing grant.",
            path: ["grantId"],
        });
    }
    if (authorization.state === "crm_lead_unresolved"
        && authorization.currentCrmLeadExternalId !== null) {
        context.addIssue({
            code: "custom",
            message: "An unresolved CRM lead must not expose a current lead id.",
            path: ["currentCrmLeadExternalId"],
        });
    }
    if (authorization.state === "context_changed" && authorization.grantId === null) {
        context.addIssue({
            code: "custom",
            message: "A changed preview context requires the stale grant for explicit revocation.",
            path: ["grantId"],
        });
    }
    if (authorization.state === "expired"
        && (authorization.grantId === null || authorization.expiresAt === null)) {
        context.addIssue({
            code: "custom",
            message: "An expired preview authorization requires the exact grant and expiration time.",
            path: [authorization.grantId === null ? "grantId" : "expiresAt"],
        });
    }
});
export const WorkspacePolicyRegenerationSchema = z.object({
    state: z.enum([
        "needs_scenario_update",
        "needs_preview_restart",
        "ready",
        "unavailable",
    ]),
    scenarioId: z.string().uuid(),
    blockedDraftRevisionId: z.string().uuid(),
    blockedPublishedVersion: z.number().int().positive(),
    currentPublishedVersion: z.number().int().positive(),
    canRegenerate: z.boolean(),
}).strict().superRefine((regeneration, context) => {
    if (regeneration.canRegenerate && regeneration.state !== "ready") {
        context.addIssue({
            code: "custom",
            message: "Policy preview regeneration is enabled only when its server state is ready.",
            path: ["canRegenerate"],
        });
    }
});
export const WorkspacePostBookingPreviewItemSchema = z.object({
    provider: z.enum(["amoCRM", "Google Таблицы", "Календарь Вехи", "Wazzup"]),
    title: z.string().trim().min(1).max(300),
    detail: z.string().trim().min(1).max(1_000).nullable(),
}).strict();
export const WorkspaceActiveNextCheckSchema = z.object({
    runId: z.string().uuid(),
    state: z.enum(["waiting", "cancelled"]),
    wakeAt: z.string().datetime().nullable(),
    intention: StoredNextCheckIntentionSchema.trim().min(5),
    reason: z.string().trim().min(5).max(600).nullable(),
    executorName: z.string().trim().min(1).max(200).nullable(),
    providerTaskId: z.string().regex(/^[1-9][0-9]{0,15}$/u).nullable(),
    taskDueAt: z.string().datetime().nullable(),
    integrity: z.enum(["matched", "replacement_pending", "missing_task", "stale_task"]),
}).strict();
export const CrmDealNextCheckSchema = WorkspaceActiveNextCheckSchema.extend({
    conversationId: z.string().uuid(),
    channelLabel: z.string().trim().min(1).max(300),
}).strict();
/** Retry of a first contact that Vekha refused locally before any provider
 * request. The same approved command is re-dispatched and re-reads amoCRM. */
export const FirstContactRetryFenceSchema = z.object({
    expectedRevision: z.number().int().nonnegative(),
    expectedManagerFence: z.number().int().nonnegative(),
    failedAttempt: z.number().int().positive(),
}).strict();
export const FirstContactRetryMutationSchema = z.object({
    expectedRevision: z.number().int().nonnegative(),
    expectedManagerFence: z.number().int().nonnegative(),
    idempotencyKey: z.string().min(16).max(160),
    reason: z.string().trim().min(5).max(1_000),
}).strict();
export const FirstContactRetryResultSchema = z.object({
    receiptId: z.string().uuid(),
    effectId: z.string().uuid(),
    replayed: z.boolean(),
    state: z.string().min(1).max(40),
}).strict();
export const WorkspaceConversationSchema = z.object({
    id: z.string().uuid(),
    expectedRevision: z.number().int().nonnegative(),
    control: z.object({
        mode: z.enum(["employee", "human"]),
        revision: z.number().int().nonnegative(),
        changedAt: z.string().datetime().nullable(),
        changedBy: z.string().min(1).max(200).nullable(),
    }).strict().optional(),
    conversationKind: z.object({
        kind: z.enum(["customer", "service"]),
        revision: z.number().int().nonnegative(),
        changedAt: z.string().datetime().nullable(),
        changedBy: z.string().min(1).max(200).nullable(),
        ignoredEventCount: z.number().int().nonnegative(),
        lastIgnoredAt: z.string().datetime().nullable(),
        proposedMarkService: z.boolean(),
        proposedReason: z.string().trim().min(5).max(1_000).nullable(),
    }).strict().default({
        kind: "customer",
        revision: 0,
        changedAt: null,
        changedBy: null,
        ignoredEventCount: 0,
        lastIgnoredAt: null,
        proposedMarkService: false,
        proposedReason: null,
    }),
    customerName: z.string().min(1).max(300),
    initials: z.string().min(1).max(4),
    stage: z.string().min(1).max(200),
    stageId: z.string().uuid().nullable().optional(),
    channel: z.string().min(1).max(100),
    lastMessage: z.string().max(4_000),
    waitingMinutes: z.number().int().nonnegative(),
    unread: z.number().int().nonnegative(),
    owner: z.string().min(1).max(200),
    messages: z.array(WorkspaceMessageSchema).max(200),
    messageTail: z.object({
        hasEarlierMessages: z.boolean(),
        includedCount: z.number().int().min(0).max(200),
    }),
    deliveryProgress: z.object({
        draftRevisionId: z.string().uuid(),
        confirmedCount: z.number().int().nonnegative(),
        totalCount: z.number().int().positive().max(20),
        updatedAt: z.string().datetime(),
        items: z.array(z.object({
            id: z.string().uuid(),
            kind: z.enum(["message", "media"]),
            sequenceNumber: z.number().int().positive().max(20),
            preview: z.string().max(4_000).nullable(),
            state: z.enum([
                "pending", "leased", "dispatching", "confirmed", "retryable",
                "ambiguous", "terminal_failed", "cancelled",
            ]),
            prerequisiteEffectCommandId: z.string().uuid().nullable(),
            availableAt: z.string().datetime(),
            dispatchStartedAt: z.string().datetime().nullable(),
            confirmedAt: z.string().datetime().nullable(),
            errorCode: z.string().trim().min(1).max(200).nullable(),
            errorMessage: z.string().trim().min(1).max(1_000).nullable(),
            // Present only for an owner/admin when the server proved this local
            // address refusal unsent and still in Yana's scope; echoed back as fences.
            retryAfterContactFix: FirstContactRetryFenceSchema.nullable().optional(),
        }).strict()).min(1).max(20),
    }).strict().nullable().default(null),
    operatorState: ConversationOperatorStateSchema,
    policyRegeneration: WorkspacePolicyRegenerationSchema.nullable().optional(),
    draftRevisionId: z.string().uuid().nullable(),
    draft: z.string().max(4_000),
    draftSummary: z.string().max(600),
    coverage: z.array(z.object({
        label: z.string().min(1).max(500),
        status: z.enum(["covered", "missing"]),
    })).max(30),
    suggestedActions: z.array(SuggestedActionSchema).max(10).default([]),
    excludedActions: z.array(ExcludedDraftActionSchema).max(10).default([]),
    postBookingPreview: z.array(WorkspacePostBookingPreviewItemSchema).max(20).default([]),
    postBookingPreviewFenceId: z.string().uuid().nullable().default(null),
    // A focused read may render the package before the browser has confirmed
    // that this exact card remained visible. Only an acknowledged fence can be
    // used for manual authorization.
    postBookingPreviewFenceReady: z.boolean().default(false),
    replyDirective: z.object({
        mode: z.enum(["reply", "no_reply"]),
        reason: z.string().trim().min(5).max(1_000).nullable(),
    }).default({ mode: "reply", reason: null }),
    replyChannelDirective: ReplyChannelDirectiveSchema,
    availableDeliveryTargets: z.array(AvailableDeliveryTargetSchema).max(10).default([]),
    activeNextCheck: WorkspaceActiveNextCheckSchema.nullable().optional(),
    followUpDirective: z.object({
        action: z.enum(["keep", "reschedule", "cancel"]),
        scheduleId: z.string().uuid().nullable(),
        dueAt: z.string().datetime().nullable(),
        reason: z.string().trim().min(5).max(1_000).nullable(),
    }).default({
        action: "keep",
        scheduleId: null,
        dueAt: null,
        reason: null,
    }),
    nextCheckDirective: z.object({
        action: z.enum(["none", "schedule"]),
        dueAt: z.string().datetime().nullable(),
        intention: z.string().trim().min(5).max(600).nullable(),
        reason: z.string().trim().min(5).max(600).nullable(),
    }).default({
        action: "none",
        dueAt: null,
        intention: null,
        reason: null,
    }),
    policyReasons: z.array(DraftPolicyReasonSchema).max(100).default([]),
    policyEvaluationStatus: z.enum([
        "persisted",
        "legacy_unavailable",
        "not_applicable",
    ]),
    knowledgeEvidence: z.object({
        status: z.enum(["not_configured", "no_match", "matched"]),
        algorithm: z.literal("postgres-fts-v1").nullable(),
        boundSourceCount: z.number().int().min(0).max(20),
        chunks: z.array(z.object({
            chunkId: z.string().uuid(),
            sourceId: z.string().uuid(),
            sourceVersionId: z.string().uuid(),
            sourceTitle: z.string().min(2).max(200),
            sourceVersion: z.number().int().positive(),
            ordinal: z.number().int().nonnegative(),
            excerpt: z.string().min(1).max(1_600),
            retrievalRank: z.number().int().min(1).max(8),
            citedClaims: z.array(z.string().min(1).max(600)).max(20),
        })).max(8),
    }),
    previewProcessingAuthorization: PreviewProcessingAuthorizationSchema,
    leadAuthorization: LeadAuthorizationSchema,
});
export const WorkspaceReviewItemSchema = z.object({
    id: z.string().uuid(),
    conversationId: z.string().uuid(),
    customerName: z.string().min(1).max(300),
    stage: z.string().min(1).max(200),
    waitLabel: z.string().min(1).max(100),
    reason: z.string().min(1).max(300),
    draft: z.string().min(1).max(4_000),
    messageParts: DraftMessagePartsSchema.optional(),
    changed: z.boolean(),
    approvalState: z.enum(["unreviewed", "approved_preview"]),
});
export const AgentWorkspaceKnowledgeMapEntrySchema = z.object({
    sourceTitle: z.string().trim().min(2).max(200),
    purpose: z.string().trim().min(10).max(1_000),
    useWhen: z.array(z.string().trim().min(3).max(300)).min(1).max(12),
}).strict();
export const AgentWorkspaceSchema = z.object({
    schemaVersion: z.literal("openclaw_workspace_v1"),
    identityMd: z.string().trim().min(20).max(4_000),
    soulMd: z.string().trim().min(20).max(12_000),
    agentsMd: z.string().trim().min(20).max(20_000),
    userMd: z.string().trim().min(20).max(4_000).nullable(),
    lessonsMd: z.string().trim().min(20).max(12_000).nullable(),
    knowledgeMap: z.array(AgentWorkspaceKnowledgeMapEntrySchema).max(20),
}).strict().superRefine((workspace, context) => {
    const titles = new Set();
    workspace.knowledgeMap.forEach((entry, index) => {
        const normalizedTitle = normalizePublicationListValue(entry.sourceTitle);
        if (titles.has(normalizedTitle)) {
            context.addIssue({
                code: "custom",
                message: "An agent workspace cannot map the same knowledge title twice.",
                path: ["knowledgeMap", index, "sourceTitle"],
            });
        }
        titles.add(normalizedTitle);
    });
});
export const WorkspaceEmployeeSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1).max(200),
    role: z.string().min(1).max(300),
    status: z.enum(["active", "draft"]),
    stages: z.array(z.string().min(1).max(200)).max(100),
    description: z.string().max(2_000),
    rulesCount: z.number().int().nonnegative(),
    knowledgeCount: z.number().int().nonnegative(),
    currentVersion: z.object({
        id: z.string().uuid(),
        version: z.number().int().positive(),
        role: z.string().trim().min(3).max(300),
        soul: z.string().trim().min(20).max(8_000),
        runtimeBrief: z.string().trim().min(20).max(2_000),
        speakingStyle: z.string().trim().min(20).max(4_000),
        boundaries: z.array(z.string().trim().min(3).max(500)).min(1).max(50),
        workspace: AgentWorkspaceSchema.optional(),
        modelConnectorAccountId: z.string().uuid(),
        model: z.string().trim().min(1).max(100),
        responseLanguage: z.string().trim().min(2).max(50),
        knowledgeSourceIds: z.array(z.string().uuid()).max(20),
        publishedAt: z.string().datetime(),
    }).strict().nullable().default(null),
    versionHistory: z.array(z.object({
        id: z.string().uuid(),
        version: z.number().int().positive(),
        publishedAt: z.string().datetime(),
        contentHash: z.string().regex(/^[0-9a-f]{64}$/u),
        current: z.boolean(),
    }).strict()).max(200).default([]),
    operatorRuntime: z.object({
        kind: z.literal("openclaw"),
        agentId: z.string().trim().min(1).max(100),
        telegramUsername: z.string().trim().regex(/^[A-Za-z0-9_]{5,32}$/u),
        telegramUrl: z.string().url(),
        avatarUrl: z.string().trim().startsWith("/").max(500),
    }).strict().nullable().default(null),
});
export const WorkspaceModelRuntimeSchema = z.object({
    connectorAccountId: z.string().uuid(),
    label: z.string().trim().min(1).max(200),
    model: z.string().trim().min(1).max(100),
    reasoningEffort: z.enum(["low", "medium", "high", "xhigh", "max"]).nullable().default(null),
    ready: z.boolean(),
    reason: z.string().trim().min(1).max(600),
}).strict();
export const ScenarioChannelScopeSchema = z.discriminatedUnion("mode", [
    z.object({
        mode: z.literal("all"),
        providerRouteIds: z.array(z.string().uuid()).length(0),
    }).strict(),
    z.object({
        mode: z.literal("selected"),
        providerRouteIds: z.array(z.string().uuid()).min(1).max(100),
    }).strict(),
]).superRefine((scope, context) => {
    if (new Set(scope.providerRouteIds).size !== scope.providerRouteIds.length) {
        context.addIssue({
            code: "custom",
            message: "A channel route cannot be selected more than once.",
            path: ["providerRouteIds"],
        });
    }
});
export const WorkspaceWorkflowRouteConflictSchema = z.object({
    assignmentId: z.string().uuid(),
    routeContractVersion: z.enum(["legacy_stage_v1", "workflow_v1"]),
    scenarioId: z.string().uuid(),
    scenarioName: z.string().trim().min(1).max(200),
    scenarioVersionId: z.string().uuid(),
    scenarioVersion: z.number().int().positive(),
    scenarioActivationId: z.string().uuid().nullable(),
    employeeId: z.string().uuid(),
    employeeName: z.string().trim().min(1).max(200),
}).strict().superRefine((conflict, context) => {
    if (conflict.routeContractVersion === "workflow_v1" && conflict.scenarioActivationId === null) {
        context.addIssue({
            code: "custom",
            message: "An active workflow route conflict requires its exact activation.",
            path: ["scenarioActivationId"],
        });
    }
    if (conflict.routeContractVersion === "legacy_stage_v1" && conflict.scenarioActivationId !== null) {
        context.addIssue({
            code: "custom",
            message: "A legacy route conflict cannot claim workflow activation identity.",
            path: ["scenarioActivationId"],
        });
    }
});
export const WorkspaceWorkflowNodeSchema = z.object({
    id: z.string().uuid(),
    nodeKey: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/u),
    displayOrder: z.number().int().min(0).max(999),
    nodeKind: z.enum(["work", "handoff", "terminal"]),
    crmStageId: z.string().uuid(),
    crmStageName: z.string().trim().min(1).max(200),
    executorEmployeeVersionId: z.string().uuid().nullable(),
    executorEmployeeName: z.string().trim().min(1).max(200).nullable(),
    activeRouteConflict: WorkspaceWorkflowRouteConflictSchema.nullable(),
    runtimeGuidance: z.object({
        allowedActionTypes: z.array(ScenarioActionTypeSchema).max(12).nullable().default(null),
        followUpText: z.string().trim().min(1).max(1_000).nullable().default(null),
        title: z.string().trim().min(1).max(120),
        goal: z.string().trim().min(20).max(2_000),
        instructions: z.array(z.string().trim().min(1).max(1_000)).min(1).max(40),
    }).strict().nullable().default(null),
    content: z.object({
        title: z.string().trim().min(1).max(120),
        goal: z.string().trim().min(20).max(2_000),
        instructions: z.array(z.string().trim().min(1).max(1_000)).min(1).max(40),
        requiredQuestions: z.array(z.object({
            id: z.string().trim().min(1).max(120),
            text: z.string().trim().min(2).max(500),
        }).strict()).max(30),
        prohibitedPhrases: z.array(z.object({
            id: z.string().trim().min(1).max(120),
            value: z.string().trim().min(2).max(300),
            matching: z.enum(["exact", "normalized"]),
        }).strict()).max(50),
        allowedActionTypes: ScenarioActionTypesSchema,
        handoffRules: z.array(z.object({
            id: z.string().trim().min(1).max(120),
            trigger: z.enum(["customer_requests_human", "policy_block", "unknown_answer", "custom"]),
            description: z.string().trim().min(3).max(600),
            operatorInstruction: z.string().trim().min(3).max(600),
        }).strict()).min(1).max(20),
        channelScope: ScenarioChannelScopeSchema,
        responsePolicy: ScenarioWorkflowResponsePolicySchema.default({
            initialDelaySeconds: 0,
            workingWindow: { mode: "always" },
        }),
        followUpPolicy: ScenarioWorkflowFollowUpPolicySchema.default({
            mode: "off",
            steps: [],
        }),
        knowledgeBindings: z.array(z.object({
            sourceId: z.string().uuid(),
            sourceVersionId: z.string().uuid(),
            title: z.string().trim().min(2).max(200),
            version: z.number().int().positive(),
            sourceOrder: z.number().int().min(0).max(19),
        }).strict()).max(20),
        mediaBindings: z.array(ScenarioWorkflowMediaBindingSchema.extend({
            title: z.string().trim().min(2).max(200),
            mediaKind: CompanyMediaKindSchema,
            fileName: z.string().trim().min(1).max(240),
            active: z.boolean(),
            currentContentSha256: z.string().regex(/^[0-9a-f]{64}$/u),
        }).strict()).max(40).optional(),
    }).strict(),
}).strict();
export const WorkspaceWorkflowTransitionSchema = z.object({
    id: z.string().uuid(),
    sourceNodeKey: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/u),
    outcomeKey: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/u),
    displayOrder: z.number().int().min(0).max(999),
    disposition: z.enum(["node", "handoff", "terminal"]),
    targetNodeKey: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/u).nullable(),
}).strict();
export const WorkspaceWorkflowScenarioSchema = z.object({
    id: z.string().uuid(),
    name: z.string().trim().min(1).max(200),
    dialogCount: z.number().int().nonnegative().default(0),
    primaryEmployeeId: z.string().uuid(),
    primaryEmployeeName: z.string().trim().min(1).max(200),
    latestVersion: z.object({
        id: z.string().uuid(),
        version: z.number().int().positive(),
        scenarioDigest: z.string().regex(/^[0-9a-f]{64}$/u),
        crmPipelineId: z.string().uuid(),
        crmPipelineName: z.string().trim().min(1).max(200),
        primaryEmployeeVersionId: z.string().uuid(),
        publishedAt: z.string().datetime(),
        nodes: z.array(WorkspaceWorkflowNodeSchema).min(1).max(100),
        transitions: z.array(WorkspaceWorkflowTransitionSchema).max(500),
    }).strict().nullable(),
    versionHistory: z.array(z.object({
        id: z.string().uuid(),
        version: z.number().int().positive(),
        scenarioDigest: z.string().regex(/^[0-9a-f]{64}$/u),
        publishedAt: z.string().datetime(),
        nodeCount: z.number().int().positive(),
        active: z.boolean(),
    }).strict()).max(200).default([]),
    activeActivation: z.object({
        id: z.string().uuid(),
        scenarioVersionId: z.string().uuid(),
        activatedAt: z.string().datetime(),
    }).strict().nullable(),
    activeDisplayTime: z.object({
        timezone: z.string().trim().min(3).max(100),
        responseLanguage: z.string().trim().min(2).max(50).nullable(),
    }).strict().nullable().optional(),
    operatingState: ScenarioOperatingStateSchema.default({
        mode: "off",
        scenarioActivationId: null,
        scenarioVersionId: null,
        revision: 0,
        changedAt: null,
        changedBy: null,
    }),
    deliveryWindow: ScenarioDeliveryWindowStateSchema.default({
        workingWindow: { mode: "always" },
        batchWindow: { mode: "silence", seconds: 15 },
        overridden: false,
        revision: 0,
        changedAt: null,
        changedBy: null,
    }),
    responsibleScope: ScenarioResponsibleScopeStateSchema.default({
        scope: { mode: "all", crmUserIds: [] },
        revision: 0,
        changedAt: null,
        changedBy: null,
    }),
    previewScopes: z.array(z.object({
        id: z.string().uuid(),
        stageNodeId: z.string().uuid(),
        state: z.enum(["active", "expired", "blocked"]),
        expiresAt: z.string().datetime(),
        // Human-created preview scopes remain capped at 100 by the mutation
        // contract. Autonomous mode deliberately persists an internal scope of
        // 1,000,000 conversations, so workspace read models must accept it.
        maxConversations: z.number().int().min(1).max(1_000_000),
        derivedConversationCount: z.number().int().nonnegative(),
    }).strict()).max(100).default([]),
}).strict();
export const WorkspaceChannelRouteSchema = z.object({
    id: z.string().uuid(),
    label: z.string().min(1).max(200),
});
export const WorkspaceScenarioActionCapabilitySchema = z.object({
    type: ScenarioActionTypeSchema,
    label: z.string().min(1).max(200),
    configurable: z.boolean(),
    available: z.boolean(),
    reason: z.string().min(1).max(600),
}).strict();
export const WorkspacePreviewPilotSchema = z.object({
    state: z.enum(["inactive", "active", "expired", "blocked", "context_changed"]),
    scopeId: z.string().uuid().nullable(),
    expiresAt: z.string().datetime().nullable(),
    maxConversations: z.number().int().min(1).max(1_000_000).nullable(),
    derivedConversationCount: z.number().int().nonnegative(),
    matchingConversationCount: z.number().int().nonnegative(),
    inFlightModelInvocationCount: z.number().int().nonnegative(),
    recoverableConversationIds: z.array(z.string().uuid()).max(200),
}).superRefine((pilot, context) => {
    if (pilot.state === "inactive") {
        if (pilot.scopeId !== null || pilot.expiresAt !== null || pilot.maxConversations !== null) {
            context.addIssue({
                code: "custom",
                message: "An inactive preview pilot must not expose an active scope.",
                path: ["scopeId"],
            });
        }
        if (pilot.recoverableConversationIds.length !== 0) {
            context.addIssue({
                code: "custom",
                message: "An inactive preview pilot cannot expose recoverable conversations.",
                path: ["recoverableConversationIds"],
            });
        }
        return;
    }
    if (pilot.scopeId === null || pilot.expiresAt === null || pilot.maxConversations === null) {
        context.addIssue({
            code: "custom",
            message: "A preview pilot state requires its exact bounded scope.",
            path: ["scopeId"],
        });
    }
});
export const WorkspaceScenarioSchema = z.object({
    id: z.string().uuid(),
    stageId: z.string().uuid(),
    employeeId: z.string().uuid(),
    stage: z.string().min(1).max(200),
    employee: z.string().min(1).max(200),
    employeeBrief: z.string().min(20).max(2_000).nullable(),
    runtimeContractReady: z.boolean(),
    goal: z.string().min(1).max(2_000),
    requiredQuestions: z.array(z.string().min(1).max(500)).max(30),
    prohibitedClaims: z.array(z.string().min(1).max(500)).max(50),
    allowedActionTypes: ScenarioActionTypesSchema,
    handoffRule: z.string().min(1).max(600),
    publishedVersion: z.number().int().positive(),
    channelScope: ScenarioChannelScopeSchema,
    knowledgeBindings: z.array(z.object({
        sourceId: z.string().uuid(),
        sourceVersionId: z.string().uuid(),
        title: z.string().min(2).max(200),
        version: z.number().int().positive(),
        currentVersion: z.number().int().positive(),
        availability: z.enum(["active", "archived"]),
    })).max(20),
    previewPilot: WorkspacePreviewPilotSchema.optional(),
    origin: z.null().default(null),
    enabled: z.boolean(),
});
export const WorkspaceKnowledgeSourceSchema = z.object({
    id: z.string().uuid(),
    title: z.string().min(2).max(200),
    availability: z.enum(["active", "archived"]),
    currentVersion: z.number().int().positive(),
    characterCount: z.number().int().min(20).max(200_000),
    chunkCount: z.number().int().min(1).max(1_000),
    updatedAt: z.string().datetime(),
    updatedBy: z.string().min(1).max(200),
    scenarioCount: z.number().int().nonnegative(),
});
export const KnowledgeSourceDetailSchema = WorkspaceKnowledgeSourceSchema.extend({
    sourceVersionId: z.string().uuid(),
    content: z.string().min(20).max(200_000),
    changeNote: z.string().min(5).max(1_000),
    versions: z.array(z.object({
        id: z.string().uuid(),
        version: z.number().int().positive(),
        title: z.string().min(2).max(200),
        availability: z.enum(["active", "archived"]),
        changeNote: z.string().min(5).max(1_000),
        createdAt: z.string().datetime(),
        createdBy: z.string().min(1).max(200),
    })).min(1).max(200),
});
export const WorkspaceAuditEventSchema = z.object({
    id: z.string().uuid(),
    title: z.string().min(1).max(200),
    detail: z.string().min(1).max(600),
    actor: z.string().min(1).max(200),
    time: z.string().min(1).max(100),
    tone: z.enum(["neutral", "safe", "warning"]),
});
export const WorkspaceLearningSignalSchema = z.object({
    employeeId: z.string().uuid(),
    employeeVersionId: z.string().uuid(),
    employeeName: z.string().trim().min(1).max(200),
    stageNodeId: z.string().uuid().nullable(),
    stageTitle: z.string().trim().min(1).max(200).nullable(),
    correctionCount: z.number().int().positive().max(10_000),
    status: z.enum(["collecting", "ready_for_review"]),
    recommendation: z.string().trim().min(5).max(1_000),
    examples: z.array(z.object({
        originalMessage: z.string().min(1).max(4_000),
        correctedMessage: z.string().min(1).max(4_000),
        reason: z.string().trim().min(5).max(1_000),
        createdAt: z.string().datetime(),
    }).strict()).min(1).max(3),
}).strict();
export const WorkspaceStageOptionSchema = z.object({
    id: z.string().uuid(),
    pipelineId: z.string().uuid().nullable().default(null),
    name: z.string().min(1).max(200),
    pipelineName: z.string().min(1).max(200),
    sortOrder: z.number().int().default(0),
    assigned: z.boolean(),
});
export const WorkspaceSnapshotSchema = z.object({
    conversations: z.array(WorkspaceConversationSchema).max(200),
    reviewItems: z.array(WorkspaceReviewItemSchema).max(200),
    employees: z.array(WorkspaceEmployeeSchema).max(100),
    scenarios: z.array(WorkspaceScenarioSchema).max(300),
    workflowScenarios: z.array(WorkspaceWorkflowScenarioSchema).max(100).default([]),
    modelRuntimes: z.array(WorkspaceModelRuntimeSchema).max(20).default([]),
    knowledgeSources: z.array(WorkspaceKnowledgeSourceSchema).max(500),
    mediaAssets: z.array(WorkspaceCompanyMediaAssetSchema).max(500).default([]),
    learningSignals: z.array(WorkspaceLearningSignalSchema).max(200).default([]),
    auditEvents: z.array(WorkspaceAuditEventSchema).max(100),
    availableStages: z.array(WorkspaceStageOptionSchema).max(1_000).default([]),
    availableChannelRoutes: z.array(WorkspaceChannelRouteSchema).max(1_000).default([]),
    scenarioActionCapabilities: z
        .array(WorkspaceScenarioActionCapabilitySchema)
        .max(10)
        .default([]),
    scheduleWorkspace: ScheduleWorkspaceSchema.default({
        connectorAccounts: [],
        crmManagers: [],
        crmStages: [],
        bindings: [],
    }),
});
export const DraftEditSchema = z.object({
    messageText: z.string().trim().min(1).max(4_000),
    messageParts: DraftMessagePartsSchema.optional(),
    reason: z.string().trim().min(5).max(1_000),
    replyChannelTargetId: z.string().uuid().nullable().optional(),
}).superRefine((edit, context) => {
    if (edit.messageParts && edit.messageParts[0]?.trim() !== edit.messageText) {
        context.addIssue({
            code: "custom",
            path: ["messageParts", 0],
            message: "The first message part must match messageText.",
        });
    }
});
export const DraftDecisionSchema = z.object({
    decision: z.enum(["approve", "reject"]),
    reason: z.string().trim().min(5).max(1_000),
    requestSend: z.boolean().default(false),
    idempotencyKey: z.string().min(16).max(300),
    postBookingPreviewFenceId: z.string().uuid().nullable().optional(),
    replyChannelTargetId: z.string().uuid().nullable().optional(),
});
export const DraftDecisionResultSchema = z.object({
    status: z.enum([
        "approved_preview",
        "queued_for_delivery",
        "no_reply_needed",
        "rejected",
    ]),
    approvalId: z.string().uuid(),
    effectCommandId: z.string().uuid().nullable(),
});
export const PostBookingPreviewReviewMutationSchema = z.object({
    fenceId: z.string().uuid(),
}).strict();
export const PostBookingPreviewReviewResultSchema = z.object({
    status: z.literal("reviewed"),
}).strict();
export const AllowlistMutationSchema = z.discriminatedUnion("enabled", [
    z.object({
        enabled: z.literal(true),
        expectedCrmLeadExternalId: z.string().trim().min(1).max(500),
        reason: z.string().trim().min(5).max(1_000),
        expiresAt: z.string().datetime().nullable(),
        currentPassword: PasswordInputSchema,
        mfaCode: MfaAuthorizationCodeSchema,
    }).strict(),
    z.object({
        enabled: z.literal(false),
        expectedAllowlistId: z.string().uuid(),
        reason: z.string().trim().min(5).max(1_000),
    }).strict(),
]);
export const AllowlistMutationResultSchema = z.object({
    enabled: z.boolean(),
    replayed: z.boolean(),
    allowlistId: z.string().uuid().nullable(),
    currentCrmLeadExternalId: z.string().min(1).max(500).nullable(),
    cancelledEffectCount: z.number().int().nonnegative(),
    inFlightCount: z.number().int().nonnegative(),
});
export const PreviewProcessingGrantMutationSchema = z.discriminatedUnion("enabled", [
    z.object({
        enabled: z.literal(true),
        expectedCrmLeadExternalId: z.string().trim().min(1).max(500),
        reason: z.string().trim().min(5).max(1_000),
        expiresAt: z.string().datetime().nullable(),
        currentPassword: PasswordInputSchema,
        mfaCode: MfaAuthorizationCodeSchema,
    }).strict(),
    z.object({
        enabled: z.literal(false),
        expectedGrantId: z.string().uuid(),
        reason: z.string().trim().min(5).max(1_000),
    }).strict(),
]);
export const PreviewProcessingGrantMutationResultSchema = z.object({
    enabled: z.boolean(),
    replayed: z.boolean(),
    grantId: z.string().uuid().nullable(),
    currentCrmLeadExternalId: z.string().min(1).max(500).nullable(),
    inFlightModelInvocationCount: z.number().int().nonnegative(),
});
export const PreviewPilotScopeMutationSchema = z.discriminatedUnion("enabled", [
    z.object({
        enabled: z.literal(true),
        expiresAt: z.string().datetime(),
        maxConversations: z.number().int().min(1).max(100),
        reason: z.string().trim().min(5).max(1_000),
        idempotencyKey: z.string().min(16).max(160),
    }).strict(),
    z.object({
        enabled: z.literal(false),
        expectedScopeId: z.string().uuid(),
        reason: z.string().trim().min(5).max(1_000),
        idempotencyKey: z.string().min(16).max(160),
    }).strict(),
]).superRefine((mutation, context) => {
    if (!mutation.enabled)
        return;
    const expiresAt = Date.parse(mutation.expiresAt);
    const now = Date.now();
    if (expiresAt <= now || expiresAt > now + 30 * 24 * 60 * 60 * 1_000) {
        context.addIssue({
            code: "custom",
            message: "A preview pilot must expire within 30 days.",
            path: ["expiresAt"],
        });
    }
});
export const PreviewPilotScopeMutationResultSchema = z.object({
    enabled: z.boolean(),
    replayed: z.boolean(),
    scopeId: z.string().uuid(),
    expiresAt: z.string().datetime(),
    maxConversations: z.number().int().min(1).max(100),
    derivedConversationCount: z.number().int().nonnegative(),
    matchingConversationCount: z.number().int().nonnegative(),
    inFlightModelInvocationCount: z.number().int().nonnegative(),
});
export const EmployeeMutationSchema = z.object({
    name: z.string().trim().min(2).max(200),
    description: z.string().trim().min(20).max(2_000),
    enabled: z.boolean().default(true),
});
export const WorkflowScenarioCreateMutationSchema = z.object({
    name: z.string().trim().min(2).max(200),
    primaryEmployeeId: z.string().uuid(),
    reason: z.string().trim().min(5).max(1_000),
    idempotencyKey: z.string().trim().min(16).max(160),
}).strict();
export const WorkflowScenarioCreateResultSchema = z.object({
    scenarioId: z.string().uuid(),
    replayed: z.boolean(),
}).strict();
export const WorkflowPreviewPilotMutationSchema = z.discriminatedUnion("enabled", [
    z.object({
        enabled: z.literal(true),
        scenarioVersionId: z.string().uuid(),
        expectedActivationId: z.string().uuid(),
        stageNodeIds: z.array(z.string().uuid()).min(1).max(100),
        expiresAt: z.string().datetime(),
        maxConversationsPerNode: z.number().int().min(1).max(100),
        reason: z.string().trim().min(5).max(1_000),
        idempotencyKey: z.string().trim().min(16).max(120),
    }).strict(),
    z.object({
        enabled: z.literal(false),
        expectedScopeIds: z.array(z.string().uuid()).min(1).max(100),
        reason: z.string().trim().min(5).max(1_000),
        idempotencyKey: z.string().trim().min(16).max(120),
    }).strict(),
]).superRefine((mutation, context) => {
    const selectedIds = mutation.enabled ? mutation.stageNodeIds : mutation.expectedScopeIds;
    if (new Set(selectedIds).size !== selectedIds.length) {
        context.addIssue({
            code: "custom",
            message: mutation.enabled
                ? "A workflow stage node cannot be selected more than once."
                : "A workflow preview scope cannot be selected more than once.",
            path: [mutation.enabled ? "stageNodeIds" : "expectedScopeIds"],
        });
    }
    if (!mutation.enabled)
        return;
    const expiresAt = Date.parse(mutation.expiresAt);
    const now = Date.now();
    if (expiresAt <= now || expiresAt > now + 30 * 24 * 60 * 60 * 1_000) {
        context.addIssue({
            code: "custom",
            message: "A workflow preview pilot must expire within 30 days.",
            path: ["expiresAt"],
        });
    }
});
export const WorkflowPreviewPilotResultSchema = z.object({
    enabled: z.boolean(),
    replayed: z.boolean(),
    scopes: z.array(z.object({
        scopeId: z.string().uuid(),
        stageNodeId: z.string().uuid(),
        expiresAt: z.string().datetime(),
        maxConversations: z.number().int().min(1).max(100),
        derivedConversationCount: z.number().int().nonnegative(),
    }).strict()).max(100),
    inFlightModelInvocationCount: z.number().int().nonnegative(),
}).strict();
function normalizePublicationListValue(value) {
    return value
        .normalize("NFKC")
        .trim()
        .toLocaleLowerCase("ru-RU")
        .replace(/\s+/gu, " ");
}
export const PublicationKnowledgeBindingSchema = z.object({
    sourceId: z.string().uuid(),
    sourceVersionId: z.string().uuid(),
    sourceOrder: z.number().int().min(0).max(19),
}).strict();
export const PublicationKnowledgeBindingsSchema = z
    .array(PublicationKnowledgeBindingSchema)
    .max(20)
    .superRefine((bindings, context) => {
    const sourceIds = new Set();
    const sourceVersionIds = new Set();
    bindings.forEach((binding, index) => {
        if (binding.sourceOrder !== index) {
            context.addIssue({
                code: "custom",
                message: "Knowledge bindings must use exact contiguous source order.",
                path: [index, "sourceOrder"],
            });
        }
        if (sourceIds.has(binding.sourceId)) {
            context.addIssue({
                code: "custom",
                message: "A knowledge source cannot be bound more than once.",
                path: [index, "sourceId"],
            });
        }
        if (sourceVersionIds.has(binding.sourceVersionId)) {
            context.addIssue({
                code: "custom",
                message: "A knowledge source version cannot be bound more than once.",
                path: [index, "sourceVersionId"],
            });
        }
        sourceIds.add(binding.sourceId);
        sourceVersionIds.add(binding.sourceVersionId);
    });
});
export const CustomerAgentKnowledgePublicationMutationSchema = z.object({
    expectedActiveKnowledgeSetId: z.string().uuid().nullable(),
    knowledgeBindings: PublicationKnowledgeBindingsSchema,
    reason: z.string().trim().min(5).max(1_000),
    idempotencyKey: z.string().trim().min(16).max(160),
}).strict();
export const CustomerAgentKnowledgePublicationResultSchema = z.object({
    employeeId: z.string().uuid(),
    knowledgeSetId: z.string().uuid(),
    version: z.number().int().positive(),
    bindingHash: z.string().regex(/^[0-9a-f]{64}$/u),
    knowledgeBindingCount: z.number().int().min(0).max(20),
    activeRevision: z.number().int().positive(),
    publishedAt: z.string().datetime(),
    replayed: z.boolean(),
}).strict();
export const EmployeeVersionSnapshotSchema = z.object({
    name: z.string().trim().min(2).max(200),
    role: z.string().trim().min(3).max(300),
    soul: z.string().trim().min(20).max(8_000),
    runtimeBrief: z.string().trim().min(20).max(2_000),
    speakingStyle: z.string().trim().min(20).max(4_000),
    boundaries: z.array(z.string().trim().min(3).max(500)).min(1).max(50),
    workspace: AgentWorkspaceSchema.optional(),
}).strict().superRefine((snapshot, context) => {
    const boundaries = new Set();
    snapshot.boundaries.forEach((boundary, index) => {
        const normalized = normalizePublicationListValue(boundary);
        if (boundaries.has(normalized)) {
            context.addIssue({
                code: "custom",
                message: "An employee boundary cannot be duplicated.",
                path: ["boundaries", index],
            });
        }
        boundaries.add(normalized);
    });
});
export const EmployeeVersionRuntimePolicySchema = z.object({
    modelConnectorAccountId: z.string().uuid(),
    model: z.string().trim().min(1).max(100),
    responseLanguage: z.string().trim().min(2).max(50),
}).strict();
export const EmployeeVersionPublicationMutationSchema = z.object({
    expectedCurrentVersionId: z.string().uuid().nullable(),
    snapshot: EmployeeVersionSnapshotSchema,
    runtimePolicy: EmployeeVersionRuntimePolicySchema,
    knowledgeBindings: PublicationKnowledgeBindingsSchema,
    reason: z.string().trim().min(5).max(1_000),
    idempotencyKey: z.string().trim().min(16).max(160),
}).strict();
export const EmployeeVersionPublicationResultSchema = z.object({
    employeeId: z.string().uuid(),
    employeeVersionId: z.string().uuid(),
    version: z.number().int().positive(),
    contentHash: z.string().regex(/^[0-9a-f]{64}$/u),
    knowledgeBindingCount: z.number().int().min(0).max(20),
    publishedAt: z.string().datetime(),
    replayed: z.boolean(),
}).strict();
const ScenarioWorkflowKeySchema = z.string().regex(/^[a-z][a-z0-9_]{0,63}$/u);
const PublishedScenarioActionTypesSchema = z
    .array(z.enum([
    "crm_note",
    "crm_stage",
    "booking",
    "booking_cancel",
    "send_media",
    "reminder",
]))
    .max(6)
    .superRefine((actions, context) => {
    if (new Set(actions).size !== actions.length) {
        context.addIssue({
            code: "custom",
            message: "A published scenario action cannot be selected more than once.",
        });
    }
});
export const ScenarioWorkflowRequiredQuestionSchema = z.object({
    id: z.string().trim().min(1).max(120),
    text: z.string().trim().min(2).max(500),
}).strict();
export const ScenarioWorkflowProhibitedPhraseSchema = z.object({
    id: z.string().trim().min(1).max(120),
    value: z.string().trim().min(2).max(300),
    matching: z.enum(["exact", "normalized"]),
}).strict();
export const ScenarioWorkflowHandoffRuleSchema = z.object({
    id: z.string().trim().min(1).max(120),
    trigger: z.enum([
        "customer_requests_human",
        "policy_block",
        "unknown_answer",
        "custom",
    ]),
    description: z.string().trim().min(3).max(600),
    operatorInstruction: z.string().trim().min(3).max(600),
}).strict();
export const ScenarioWorkflowNodeContentSchema = z.object({
    title: z.string().trim().min(1).max(120),
    goal: z.string().trim().min(20).max(2_000),
    instructions: z.array(z.string().trim().min(1).max(1_000)).min(1).max(40),
    requiredQuestions: z.array(ScenarioWorkflowRequiredQuestionSchema).max(30),
    prohibitedPhrases: z.array(ScenarioWorkflowProhibitedPhraseSchema).max(50),
    allowedActionTypes: PublishedScenarioActionTypesSchema,
    handoffRules: z.array(ScenarioWorkflowHandoffRuleSchema).min(1).max(20),
    channelScope: ScenarioChannelScopeSchema,
    knowledgeBindings: PublicationKnowledgeBindingsSchema,
    mediaBindings: z.array(ScenarioWorkflowMediaBindingSchema).max(40).default([]),
    responsePolicy: ScenarioWorkflowResponsePolicySchema.default({
        initialDelaySeconds: 0,
        workingWindow: { mode: "always" },
    }),
    followUpPolicy: ScenarioWorkflowFollowUpPolicySchema.default({
        mode: "off",
        steps: [],
    }),
}).strict().superRefine((content, context) => {
    const mediaKeys = new Set();
    const mediaAssetIds = new Set();
    content.mediaBindings.forEach((binding, index) => {
        if (binding.sourceOrder !== index) {
            context.addIssue({
                code: "custom",
                message: "Media-binding order must be contiguous and match array order.",
                path: ["mediaBindings", index, "sourceOrder"],
            });
        }
        if (mediaKeys.has(binding.key)) {
            context.addIssue({
                code: "custom",
                message: "A portable media-binding key cannot be duplicated.",
                path: ["mediaBindings", index, "key"],
            });
        }
        if (mediaAssetIds.has(binding.assetId)) {
            context.addIssue({
                code: "custom",
                message: "A media asset cannot be bound to one node more than once.",
                path: ["mediaBindings", index, "assetId"],
            });
        }
        mediaKeys.add(binding.key);
        mediaAssetIds.add(binding.assetId);
    });
    const allowsMedia = content.allowedActionTypes.includes("send_media");
    if (allowsMedia !== (content.mediaBindings.length > 0)) {
        context.addIssue({
            code: "custom",
            message: "send_media requires at least one immutable node media binding, and other nodes cannot retain media bindings.",
            path: ["mediaBindings"],
        });
    }
    const questionIds = new Set();
    const questionTexts = new Set();
    content.requiredQuestions.forEach((question, index) => {
        const normalizedText = normalizePublicationListValue(question.text);
        if (questionIds.has(question.id)) {
            context.addIssue({
                code: "custom",
                message: "A required-question id cannot be duplicated.",
                path: ["requiredQuestions", index, "id"],
            });
        }
        if (questionTexts.has(normalizedText)) {
            context.addIssue({
                code: "custom",
                message: "A required question cannot be duplicated.",
                path: ["requiredQuestions", index, "text"],
            });
        }
        questionIds.add(question.id);
        questionTexts.add(normalizedText);
    });
    const prohibitedIds = new Set();
    const prohibitedValues = new Set();
    content.prohibitedPhrases.forEach((phrase, index) => {
        const normalizedValue = `${phrase.matching}:${normalizePublicationListValue(phrase.value)}`;
        if (prohibitedIds.has(phrase.id)) {
            context.addIssue({
                code: "custom",
                message: "A prohibited-phrase id cannot be duplicated.",
                path: ["prohibitedPhrases", index, "id"],
            });
        }
        if (prohibitedValues.has(normalizedValue)) {
            context.addIssue({
                code: "custom",
                message: "A prohibited phrase cannot be duplicated for one matching mode.",
                path: ["prohibitedPhrases", index, "value"],
            });
        }
        prohibitedIds.add(phrase.id);
        prohibitedValues.add(normalizedValue);
    });
    const handoffIds = new Set();
    content.handoffRules.forEach((rule, index) => {
        if (handoffIds.has(rule.id)) {
            context.addIssue({
                code: "custom",
                message: "A handoff-rule id cannot be duplicated.",
                path: ["handoffRules", index, "id"],
            });
        }
        handoffIds.add(rule.id);
    });
    if (!content.handoffRules.some((rule) => rule.trigger === "policy_block")) {
        context.addIssue({
            code: "custom",
            message: "A published workflow node must route policy blocks to an operator.",
            path: ["handoffRules"],
        });
    }
    if (!content.handoffRules.some((rule) => rule.trigger === "customer_requests_human")) {
        context.addIssue({
            code: "custom",
            message: "A published workflow node must honor a request for a person.",
            path: ["handoffRules"],
        });
    }
});
const ScenarioWorkflowNodeCommonShape = {
    nodeKey: ScenarioWorkflowKeySchema,
    displayOrder: z.number().int().min(0).max(999),
    crmStageId: z.string().uuid(),
    content: ScenarioWorkflowNodeContentSchema,
};
export const ScenarioWorkflowNodeSchema = z.discriminatedUnion("nodeKind", [
    z.object({
        ...ScenarioWorkflowNodeCommonShape,
        nodeKind: z.literal("work"),
        executorEmployeeVersionId: z.string().uuid(),
    }).strict(),
    z.object({
        ...ScenarioWorkflowNodeCommonShape,
        nodeKind: z.literal("handoff"),
        executorEmployeeVersionId: z.null(),
    }).strict(),
    z.object({
        ...ScenarioWorkflowNodeCommonShape,
        nodeKind: z.literal("terminal"),
        executorEmployeeVersionId: z.null(),
    }).strict(),
]);
export const ScenarioWorkflowNotificationPolicySchema = z.object({
    mode: z.literal("none"),
}).strict();
const ScenarioWorkflowTransitionCommonShape = {
    sourceNodeKey: ScenarioWorkflowKeySchema,
    outcomeKey: ScenarioWorkflowKeySchema,
    displayOrder: z.number().int().min(0).max(999),
    actionSet: z.array(z.never()).length(0),
    notificationPolicy: ScenarioWorkflowNotificationPolicySchema,
};
export const ScenarioWorkflowTransitionSchema = z.discriminatedUnion("disposition", [
    z.object({
        ...ScenarioWorkflowTransitionCommonShape,
        disposition: z.literal("node"),
        targetNodeKey: ScenarioWorkflowKeySchema,
    }).strict(),
    z.object({
        ...ScenarioWorkflowTransitionCommonShape,
        disposition: z.literal("handoff"),
        targetNodeKey: z.null(),
    }).strict(),
    z.object({
        ...ScenarioWorkflowTransitionCommonShape,
        disposition: z.literal("terminal"),
        targetNodeKey: z.null(),
    }).strict(),
]);
export const ScenarioWorkflowPublicationMutationSchema = z.object({
    aggregateSchemaVersion: z.literal("workflow_v1"),
    expectedLatestVersionId: z.string().uuid().nullable(),
    primaryEmployeeVersionId: z.string().uuid(),
    crmPipelineId: z.string().uuid(),
    nodes: z.array(ScenarioWorkflowNodeSchema).min(1).max(100),
    transitions: z.array(ScenarioWorkflowTransitionSchema).max(500),
    reason: z.string().trim().min(5).max(1_000),
    idempotencyKey: z.string().trim().min(16).max(160),
}).strict().superRefine((mutation, context) => {
    const nodesByKey = new Map();
    const crmStageIds = new Set();
    let workNodeCount = 0;
    let primaryEmployeeRunsWorkNode = false;
    mutation.nodes.forEach((node, index) => {
        if (node.displayOrder !== index) {
            context.addIssue({
                code: "custom",
                message: "Workflow nodes must use exact contiguous display order.",
                path: ["nodes", index, "displayOrder"],
            });
        }
        if (nodesByKey.has(node.nodeKey)) {
            context.addIssue({
                code: "custom",
                message: "A workflow node key cannot be duplicated.",
                path: ["nodes", index, "nodeKey"],
            });
        }
        if (crmStageIds.has(node.crmStageId)) {
            context.addIssue({
                code: "custom",
                message: "A CRM stage cannot be assigned to more than one workflow node.",
                path: ["nodes", index, "crmStageId"],
            });
        }
        nodesByKey.set(node.nodeKey, node);
        crmStageIds.add(node.crmStageId);
        if (node.nodeKind === "work") {
            workNodeCount += 1;
            if (node.executorEmployeeVersionId === mutation.primaryEmployeeVersionId) {
                primaryEmployeeRunsWorkNode = true;
            }
        }
    });
    if (workNodeCount === 0) {
        context.addIssue({
            code: "custom",
            message: "A scenario workflow must contain at least one work node.",
            path: ["nodes"],
        });
    }
    if (!primaryEmployeeRunsWorkNode) {
        context.addIssue({
            code: "custom",
            message: "The primary employee version must execute at least one work node.",
            path: ["primaryEmployeeVersionId"],
        });
    }
    const transitionKeys = new Set();
    const transitionOrders = new Map();
    mutation.transitions.forEach((transition, index) => {
        const sourceNode = nodesByKey.get(transition.sourceNodeKey);
        if (!sourceNode) {
            context.addIssue({
                code: "custom",
                message: "A transition source must reference a workflow node.",
                path: ["transitions", index, "sourceNodeKey"],
            });
        }
        else if (sourceNode.nodeKind !== "work") {
            context.addIssue({
                code: "custom",
                message: "Only a work node can have outgoing transitions.",
                path: ["transitions", index, "sourceNodeKey"],
            });
        }
        if (transition.disposition === "node") {
            if (!nodesByKey.has(transition.targetNodeKey)) {
                context.addIssue({
                    code: "custom",
                    message: "A transition target must reference a workflow node.",
                    path: ["transitions", index, "targetNodeKey"],
                });
            }
            if (transition.targetNodeKey === transition.sourceNodeKey) {
                context.addIssue({
                    code: "custom",
                    message: "A transition cannot target its own source node.",
                    path: ["transitions", index, "targetNodeKey"],
                });
            }
        }
        const transitionKey = `${transition.sourceNodeKey}:${transition.outcomeKey}`;
        if (transitionKeys.has(transitionKey)) {
            context.addIssue({
                code: "custom",
                message: "A transition outcome cannot be duplicated for one source node.",
                path: ["transitions", index, "outcomeKey"],
            });
        }
        transitionKeys.add(transitionKey);
        const sourceOrders = transitionOrders.get(transition.sourceNodeKey) ?? [];
        sourceOrders.push({ index, order: transition.displayOrder });
        transitionOrders.set(transition.sourceNodeKey, sourceOrders);
    });
    for (const sourceOrders of transitionOrders.values()) {
        const sorted = [...sourceOrders].sort((left, right) => left.order - right.order);
        sorted.forEach((entry, expectedOrder) => {
            if (entry.order !== expectedOrder) {
                context.addIssue({
                    code: "custom",
                    message: "Transitions from one node must use unique contiguous display order.",
                    path: ["transitions", entry.index, "displayOrder"],
                });
            }
        });
    }
});
const ScenarioWorkflowPublishedNodeCommonShape = {
    nodeId: z.string().uuid(),
    nodeKey: ScenarioWorkflowKeySchema,
    displayOrder: z.number().int().min(0).max(999),
    crmStageId: z.string().uuid(),
};
export const ScenarioWorkflowPublishedNodeSchema = z.discriminatedUnion("nodeKind", [
    z.object({
        ...ScenarioWorkflowPublishedNodeCommonShape,
        nodeKind: z.literal("work"),
        executorEmployeeVersionId: z.string().uuid(),
    }).strict(),
    z.object({
        ...ScenarioWorkflowPublishedNodeCommonShape,
        nodeKind: z.literal("handoff"),
        executorEmployeeVersionId: z.null(),
    }).strict(),
    z.object({
        ...ScenarioWorkflowPublishedNodeCommonShape,
        nodeKind: z.literal("terminal"),
        executorEmployeeVersionId: z.null(),
    }).strict(),
]);
export const ScenarioWorkflowPublicationResultSchema = z.object({
    publicationId: z.string().uuid(),
    scenarioId: z.string().uuid(),
    scenarioVersionId: z.string().uuid(),
    version: z.number().int().positive(),
    aggregateSchemaVersion: z.literal("workflow_v1"),
    scenarioDigest: z.string().regex(/^[0-9a-f]{64}$/u),
    primaryEmployeeVersionId: z.string().uuid(),
    nodes: z.array(ScenarioWorkflowPublishedNodeSchema).min(1).max(100),
    counts: z.object({
        nodeCount: z.number().int().positive().max(100),
        workNodeCount: z.number().int().positive().max(100),
        transitionCount: z.number().int().nonnegative().max(500),
        knowledgeBindingCount: z.number().int().nonnegative().max(2_000),
    }).strict(),
    activation: z.object({
        changed: z.literal(false),
        activeActivationId: z.string().uuid().nullable(),
        activeScenarioVersionId: z.string().uuid().nullable(),
    }).strict(),
    publishedAt: z.string().datetime(),
    replayed: z.boolean(),
}).strict().superRefine((result, context) => {
    const nodeIds = new Set();
    const nodeKeys = new Set();
    const stageIds = new Set();
    result.nodes.forEach((node, index) => {
        if (node.displayOrder !== index) {
            context.addIssue({
                code: "custom",
                message: "Published nodes must use exact contiguous display order.",
                path: ["nodes", index, "displayOrder"],
            });
        }
        for (const [value, values, field] of [
            [node.nodeId, nodeIds, "nodeId"],
            [node.nodeKey, nodeKeys, "nodeKey"],
            [node.crmStageId, stageIds, "crmStageId"],
        ]) {
            if (values.has(value)) {
                context.addIssue({
                    code: "custom",
                    message: `A published workflow ${field} cannot be duplicated.`,
                    path: ["nodes", index, field],
                });
            }
            values.add(value);
        }
    });
    if (result.counts.nodeCount !== result.nodes.length) {
        context.addIssue({
            code: "custom",
            message: "The publication node count must match the exact returned nodes.",
            path: ["counts", "nodeCount"],
        });
    }
    const workNodeCount = result.nodes.filter((node) => node.nodeKind === "work").length;
    if (result.counts.workNodeCount !== workNodeCount) {
        context.addIssue({
            code: "custom",
            message: "The publication work-node count must match the exact returned nodes.",
            path: ["counts", "workNodeCount"],
        });
    }
    const hasActiveActivation = result.activation.activeActivationId !== null;
    const hasActiveVersion = result.activation.activeScenarioVersionId !== null;
    if (hasActiveActivation !== hasActiveVersion) {
        context.addIssue({
            code: "custom",
            message: "An unchanged activation must expose both active ids or neither.",
            path: ["activation"],
        });
    }
});
export const ScenarioMutationSchema = z.object({
    stageId: z.string().uuid(),
    employeeId: z.string().uuid(),
    goal: z.string().trim().min(20).max(2_000),
    requiredQuestions: z.array(z.string().trim().min(2).max(500)).max(30),
    prohibitedClaims: z.array(z.string().trim().min(2).max(300)).max(50),
    allowedActionTypes: LegacyScenarioActionTypesSchema.default([]),
    handoffRule: z.string().trim().min(3).max(600),
    knowledgeSourceIds: z.array(z.string().uuid()).max(20),
    channelScope: ScenarioChannelScopeSchema.default({
        mode: "all",
        providerRouteIds: [],
    }),
    expectedPublishedVersion: z.number().int().nonnegative(),
    expectedPublishedVersionId: z.string().uuid().optional(),
    idempotencyKey: z.string().min(16).max(160),
    enabled: z.boolean(),
});
export const ScenarioActivationMutationSchema = z.object({
    scenarioVersionId: z.string().uuid(),
    expectedScenarioDigest: z.string().regex(/^[0-9a-f]{64}$/u),
    expectedActiveActivationId: z.string().uuid().nullable(),
    reason: z.string().trim().min(5).max(1_000),
    idempotencyKey: z.string().trim().min(16).max(160),
}).strict();
export const ScenarioActivationResultSchema = z.object({
    activationId: z.string().uuid(),
    scenarioId: z.string().uuid(),
    scenarioVersionId: z.string().uuid(),
    scenarioDigest: z.string().regex(/^[0-9a-f]{64}$/u),
    supersededActivationId: z.string().uuid().nullable(),
    activeRouteCount: z.number().int().positive(),
    revokedPreviewPilotCount: z.number().int().nonnegative(),
    revokedPreviewGrantCount: z.number().int().nonnegative(),
    inFlightModelInvocationCount: z.number().int().nonnegative(),
    replayed: z.boolean(),
    activatedAt: z.string().datetime(),
}).strict();
export const KnowledgeSourceMutationSchema = z.object({
    title: z.string().trim().min(2).max(200),
    content: z.string().min(20).max(200_000),
    availability: z.enum(["active", "archived"]),
    changeNote: z.string().trim().min(5).max(1_000),
    expectedVersion: z.number().int().nonnegative(),
    idempotencyKey: z.string().min(16).max(160),
});
export const KnowledgeSourceMutationResultSchema = z.object({
    sourceId: z.string().uuid(),
    sourceVersionId: z.string().uuid(),
    version: z.number().int().positive(),
    replayed: z.boolean(),
});
export const ConversationRecoveryActionSchema = z.enum([
    "retry_crm_resolution",
    "retry_generation",
    "reprepare_draft",
    "restore_missing_draft",
    "resume_after_manager_activity",
    "resume_after_policy_blocked_customer_reply",
]);
export const ConversationRecoverySchema = z.object({
    action: ConversationRecoveryActionSchema,
    expectedRevision: z.number().int().nonnegative(),
    expectedDraftRevisionId: z.string().uuid().optional(),
    expectedManagerFence: z.number().int().nonnegative().optional(),
    expectedScenarioMode: z.enum(["drafts", "live"]).optional(),
    dryRun: z.boolean().optional(),
    idempotencyKey: z.string().min(16).max(160),
    reason: z.string().trim().min(5).max(1_000),
    operatorComment: z.string().trim().min(5).max(1_000).optional(),
}).superRefine((input, context) => {
    if (input.action === "reprepare_draft" && !input.expectedDraftRevisionId) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["expectedDraftRevisionId"],
            message: "Current draft revision is required for repreparation.",
        });
    }
    if (input.operatorComment && input.action !== "reprepare_draft") {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["operatorComment"],
            message: "Operator comments are accepted only for draft repreparation.",
        });
    }
    if (input.dryRun === true && input.action !== "restore_missing_draft") {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["dryRun"],
            message: "Dry-run is accepted only for missing-work restoration.",
        });
    }
});
export const ConversationRecoveryResultSchema = z.object({
    conversationId: z.string().uuid(),
    conversationRevision: z.number().int().nonnegative(),
    jobId: z.string().uuid().nullable(),
    status: z.enum(["queued", "idempotent_replay", "dry_run"]),
    managerFence: z.number().int().nonnegative().optional(),
    scenarioMode: z.enum(["drafts", "live"]).nullable().optional(),
    sourceJobId: z.string().uuid().nullable().optional(),
    customerEffect: z.literal(false).optional(),
    savedOperatorComment: z.string().trim().min(5).max(1_000).nullable().optional(),
}).superRefine((result, context) => {
    if (result.status === "dry_run" && result.jobId !== null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["jobId"],
            message: "Dry-run recovery must not invent a job receipt.",
        });
    }
    if (result.status !== "dry_run" && result.jobId === null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["jobId"],
            message: "Queued or replayed recovery must return a durable job receipt.",
        });
    }
});
export const ConversationControlMutationSchema = z.object({
    mode: z.enum(["employee", "human"]),
    expectedRevision: z.number().int().nonnegative(),
    expectedControlRevision: z.number().int().nonnegative(),
    idempotencyKey: z.string().trim().min(16).max(160),
}).strict();
export const ConversationControlResultSchema = z.object({
    conversationId: z.string().uuid(),
    mode: z.enum(["employee", "human"]),
    conversationRevision: z.number().int().nonnegative(),
    controlRevision: z.number().int().nonnegative(),
    changedAt: z.string().datetime(),
    changedBy: z.string().min(1).max(200),
    resumedJobId: z.string().uuid().nullable(),
    replayed: z.boolean(),
}).strict();
export const ConversationKindMutationSchema = z.object({
    kind: z.enum(["customer", "service"]),
    expectedRevision: z.number().int().nonnegative(),
    expectedKindRevision: z.number().int().nonnegative(),
    idempotencyKey: z.string().trim().min(16).max(160),
    reason: z.string().trim().min(5).max(1_000).nullable().default(null),
}).strict();
export const ConversationKindResultSchema = z.object({
    conversationId: z.string().uuid(),
    kind: z.enum(["customer", "service"]),
    conversationRevision: z.number().int().nonnegative(),
    kindRevision: z.number().int().nonnegative(),
    changedAt: z.string().datetime(),
    changedBy: z.string().min(1).max(200),
    cancelledJobCount: z.number().int().nonnegative(),
    cancelledDraftCount: z.number().int().nonnegative(),
    replayed: z.boolean(),
}).strict();
export const ConversationPolicyRegenerationSchema = z.object({
    expectedRevision: z.number().int().nonnegative(),
    blockedDraftRevisionId: z.string().uuid(),
    expectedReplacementPublishedVersion: z.number().int().positive(),
    idempotencyKey: z.string().min(16).max(160),
    reason: z.string().trim().min(5).max(1_000),
}).strict();
export const ConversationPolicyRegenerationResultSchema = z.object({
    conversationId: z.string().uuid(),
    conversationRevision: z.number().int().nonnegative(),
    jobId: z.string().uuid(),
    replacementPublishedVersion: z.number().int().positive(),
    status: z.enum(["queued", "idempotent_replay"]),
}).strict();
export const CrmDealReplyStateSchema = z.enum([
    "needs_reply",
    "waiting_customer",
    "no_messages",
]);
export const CrmDealControlModeSchema = z.enum(["employee", "human"]);
export const CrmDealHelpReasonSchema = z.enum([
    "none",
    "human_control",
    "ambiguous_link",
    "no_active_conversation",
    "conversation_handoff",
]);
const CrmDealFilterListSchema = (item) => z
    .array(item)
    .max(100)
    .default([])
    .superRefine((items, context) => {
    if (new Set(items).size !== items.length) {
        context.addIssue({ code: "custom", message: "Deal filters cannot contain duplicates." });
    }
});
export const CrmDealListQuerySchema = z.object({
    search: z.string().trim().max(200).default(""),
    employeeIds: CrmDealFilterListSchema(z.string().uuid()),
    scenarioIds: CrmDealFilterListSchema(z.string().uuid()),
    conversationPresence: z.enum(["any", "active"]).default("any"),
    requiresAttention: z.boolean().default(false),
    pipelineIds: CrmDealFilterListSchema(z.string().uuid()),
    stageIds: CrmDealFilterListSchema(z.string().uuid()),
    replyStates: CrmDealFilterListSchema(CrmDealReplyStateSchema),
    controlModes: CrmDealFilterListSchema(CrmDealControlModeSchema),
    channels: CrmDealFilterListSchema(z.string().trim().min(1).max(100)),
    helpReasons: CrmDealFilterListSchema(CrmDealHelpReasonSchema),
    // Live refresh re-reads only the deals an event named.
    dealIds: CrmDealFilterListSchema(z.string().uuid()),
    cursor: z.string().trim().min(1).max(1_000).nullable().default(null),
    limit: z.number().int().min(1).max(100).default(30),
}).strict();
// `deals` names what changed since the previous event; `resync` means the
// stream could not name it (reconnect, burst) and the screen re-reads fully.
export const DialogLiveChangeSchema = z.object({
    dealIds: z.array(z.string().uuid()).max(100),
    conversationIds: z.array(z.string().uuid()).max(500),
    drafts: z.boolean(),
    escalations: z.boolean(),
}).strict();
export const CrmDealTimelineQuerySchema = z.object({
    conversationId: z.string().uuid().nullable().default(null),
    cursor: z.string().trim().min(1).max(1_000).nullable().default(null),
    limit: z.number().int().min(1).max(100).default(50),
}).strict();
export const GipnoActionLogPeriodKindSchema = z.enum([
    "today",
    "seven_days",
    "custom",
    "live_run",
]);
// Native calendar actions reuse legacy connector routing, but are not Google API calls.
export const GipnoActionLogProviderKindSchema = z.union([ConnectorKindSchema, z.literal("native_calendar")]);
export const GipnoActionLogQuerySchema = z.object({
    employeeIds: CrmDealFilterListSchema(z.string().uuid()),
    scenarioIds: CrmDealFilterListSchema(z.string().uuid()),
    messagesOnly: z.boolean().default(false),
    connectorKinds: CrmDealFilterListSchema(GipnoActionLogProviderKindSchema),
    states: CrmDealFilterListSchema(z.enum([
        "pending", "leased", "dispatching", "confirmed", "retryable",
        "ambiguous", "terminal_failed", "cancelled",
    ])),
    periodKind: GipnoActionLogPeriodKindSchema.default("today"),
    from: z.string().datetime().nullable().default(null),
    to: z.string().datetime().nullable().default(null),
    cursor: z.string().trim().min(1).max(1_000).nullable().default(null),
    limit: z.number().int().min(1).max(100).default(50),
}).strict().superRefine((query, context) => {
    if (query.periodKind === "custom" && (query.from === null || query.to === null)) {
        context.addIssue({ code: "custom", path: ["from"], message: "Custom period requires from and to." });
    }
    if (query.from !== null && query.to !== null && Date.parse(query.from) >= Date.parse(query.to)) {
        context.addIssue({ code: "custom", path: ["to"], message: "Period end must be after its start." });
    }
    if (query.periodKind === "live_run" && query.scenarioIds.length !== 1) {
        context.addIssue({ code: "custom", path: ["scenarioIds"], message: "Live period requires one scenario." });
    }
});
export const GipnoActionLogItemSchema = z.object({
    id: z.string().uuid(),
    effectType: z.enum([
        "send_message", "send_media", "crm_note", "crm_stage",
        "crm_change_responsible", "crm_task", "crm_task_complete", "crm_set_fields",
        "schedule_booking", "schedule_booking_cancel",
    ]),
    state: z.enum([
        "pending", "leased", "dispatching", "confirmed", "retryable",
        "ambiguous", "terminal_failed", "cancelled",
    ]),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    confirmedAt: z.string().datetime().nullable(),
    operationLabel: z.string().trim().min(1).max(200),
    preview: z.string().max(4_000).nullable(),
    attemptCount: z.number().int().nonnegative(),
    errorCode: z.string().trim().min(1).max(200).nullable(),
    errorMessage: z.string().trim().min(1).max(1_000).nullable(),
    agent: z.object({ id: z.string().uuid(), name: z.string().trim().min(1).max(200) }).strict(),
    scenario: z.object({ id: z.string().uuid(), name: z.string().trim().min(1).max(200), version: z.number().int().positive() }).strict(),
    deal: z.object({ id: z.string().uuid(), externalId: z.string().trim().min(1).max(200), name: z.string().trim().min(1).max(500) }).strict().nullable(),
    customerName: z.string().trim().min(1).max(500).nullable(),
    provider: z.object({
        kind: GipnoActionLogProviderKindSchema,
        label: z.string().trim().min(1).max(200),
        routeLabel: z.string().trim().min(1).max(200).nullable(),
        requestRef: z.string().trim().min(1).max(500).nullable(),
        status: z.string().trim().min(1).max(200).nullable(),
        acknowledgedAt: z.string().datetime().nullable(),
    }).strict(),
    attempts: z.array(z.object({
        number: z.number().int().positive(),
        outcome: z.enum(["dispatching", "accepted", "retryable", "ambiguous", "terminal_failed"]),
        startedAt: z.string().datetime(),
        completedAt: z.string().datetime().nullable(),
        httpStatus: z.number().int().min(100).max(599).nullable(),
        errorCode: z.string().trim().min(1).max(200).nullable(),
    }).strict()).max(100),
}).strict();
export const GipnoActionLogSchema = z.object({
    items: z.array(GipnoActionLogItemSchema).max(100),
    period: z.object({
        kind: GipnoActionLogPeriodKindSchema,
        from: z.string().datetime(),
        to: z.string().datetime(),
        liveRunFound: z.boolean(),
    }).strict(),
    nextCursor: z.string().max(1_000).nullable(),
    asOf: z.string().datetime(),
}).strict();
export const CrmDealContactSchema = z.object({
    id: z.string().uuid(),
    name: z.string().trim().min(1).max(500).nullable(),
}).strict();
export const CrmDealChannelSchema = z.object({
    key: z.string().trim().min(1).max(100),
    label: z.string().trim().min(1).max(300),
    conversationCount: z.number().int().positive(),
}).strict();
export const CrmDealLastMessageSchema = z.object({
    id: z.string().uuid(),
    conversationId: z.string().uuid(),
    channel: CrmDealChannelSchema.pick({ key: true, label: true }),
    direction: z.enum(["inbound", "outbound"]),
    actorKind: z.enum(["customer", "operator", "ai", "provider", "system"]),
    content: z.string().min(1).max(4_000),
    occurredAt: z.string().datetime(),
}).strict();
export const CrmDealSummarySchema = z.object({
    id: z.string().uuid(),
    revision: z.number().int().nonnegative(),
    externalId: z.string().trim().min(1).max(200),
    crmUrl: z.string().url().max(1_000).nullable().default(null),
    name: z.string().trim().min(1).max(500),
    isClosed: z.boolean(),
    pipeline: z.object({
        id: z.string().uuid(),
        name: z.string().trim().min(1).max(500),
    }).strict(),
    stage: z.object({
        id: z.string().uuid(),
        externalId: z.string().trim().min(1).max(200).optional(),
        name: z.string().trim().min(1).max(500),
    }).strict(),
    responsible: z.object({
        id: z.string().uuid(),
        name: z.string().trim().min(1).max(500),
    }).strict().nullable(),
    assignment: z.object({
        agent: z.object({
            id: z.string().uuid(),
            name: z.string().trim().min(1).max(200),
        }).strict(),
        scenario: z.object({
            id: z.string().uuid(),
            name: z.string().trim().min(1).max(200),
            version: z.object({
                id: z.string().uuid(),
                number: z.number().int().positive(),
            }).strict(),
            activationId: z.string().uuid().nullable(),
            mode: z.enum(["off", "preview", "drafts", "live"]),
        }).strict(),
    }).strict().nullable().default(null),
    contacts: z.array(CrmDealContactSchema).max(50),
    channels: z.array(CrmDealChannelSchema).max(50),
    activeConversationCount: z.number().int().nonnegative(),
    nextCheck: CrmDealNextCheckSchema.nullable().optional(),
    ambiguousLinkCount: z.number().int().nonnegative(),
    ownerEscalationCount: z.number().int().nonnegative().default(0),
    replyState: CrmDealReplyStateSchema,
    control: z.object({
        mode: CrmDealControlModeSchema,
        revision: z.number().int().nonnegative(),
        replyFence: z.number().int().nonnegative(),
        changedAt: z.string().datetime().nullable(),
        changedBy: z.string().trim().min(1).max(200).nullable(),
    }).strict(),
    helpReason: z.object({
        code: CrmDealHelpReasonSchema,
        conversationId: z.string().uuid().nullable().default(null),
        conversationBlockReasonCodes: z.array(z.string().trim().min(1).max(200)).max(50),
    }).strict(),
    lastMessage: CrmDealLastMessageSchema.nullable(),
    activityAt: z.string().datetime(),
    crmUpdatedAt: z.string().datetime().nullable(),
}).strict();
export const CrmDealFacetSchema = z.object({
    key: z.string().trim().min(1).max(500),
    label: z.string().trim().min(1).max(500),
    count: z.number().int().nonnegative(),
}).strict();
export const CrmDealListSchema = z.object({
    items: z.array(CrmDealSummarySchema).max(100),
    total: z.number().int().nonnegative(),
    attentionTotal: z.number().int().nonnegative().default(0),
    facets: z.object({
        pipelines: z.array(CrmDealFacetSchema).max(1_000),
        stages: z.array(CrmDealFacetSchema).max(5_000),
        replyStates: z.array(CrmDealFacetSchema).max(3),
        controlModes: z.array(CrmDealFacetSchema).max(2),
        channels: z.array(CrmDealFacetSchema).max(100),
        helpReasons: z.array(CrmDealFacetSchema).max(5),
    }).strict(),
    asOf: z.string().datetime(),
    nextCursor: z.string().max(1_000).nullable(),
}).strict();
// Events of a deal's chats shown in the dialog timeline between the messages
// (Паша 09.10.2026: «как лучше показывать события? может прям в чате?»).
export const CrmDealEventSchema = z.object({
    id: z.string().min(1).max(200),
    conversationId: z.string().uuid().nullable(),
    at: z.string().datetime(),
    kind: z.enum(["stage", "closed", "handoff", "no_reply", "next_check", "stop", "failure", "return"]),
    actor: z.string().trim().min(1).max(200),
    title: z.string().trim().min(1).max(300),
    detail: z.string().max(1_500).nullable(),
    dueAt: z.string().datetime().nullable().optional(),
}).strict();
export const CrmDealEventsSchema = z.object({
    dealId: z.string().uuid(),
    items: z.array(CrmDealEventSchema).max(300),
    asOf: z.string().datetime(),
}).strict();
// «Яна за день» (Паша 09.10.2026): the dialogs where the AI employee actually
// worked on one Moscow day, for a quick evening review. Membership follows her
// drafts in the chat, not today's CRM responsible or stage.
export const YanaDayQuerySchema = z.object({
    date: z.string().refine((value) => {
        if (!/^\d{4}-\d{2}-\d{2}$/u.test(value))
            return false;
        const date = new Date(`${value}T12:00:00Z`);
        return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
    }, "Date must be a calendar date YYYY-MM-DD."),
    employeeIds: CrmDealFilterListSchema(z.string().uuid()),
}).strict();
export const YanaDayAttentionSchema = z.enum(["failure", "unanswered", "blocked", "no_next_check"]);
export const YanaDayDealSchema = z.object({
    deal: CrmDealSummarySchema,
    day: z.object({
        conversationIds: z.array(z.string().uuid()).min(1).max(50),
        customerMessages: z.number().int().nonnegative(),
        yanaMessages: z.number().int().nonnegative(),
        managerMessages: z.number().int().nonnegative(),
        // The first message of the day opens the chat; null when the day had only decisions.
        firstMessageAt: z.string().datetime().nullable(),
        lastActivityAt: z.string().datetime(),
        lastMessage: z.object({
            party: z.enum(["customer", "ai", "manager", "system"]),
            content: z.string().min(1).max(4_000),
            occurredAt: z.string().datetime(),
        }).strict().nullable(),
        // The day's last customer message has no reply and no decision after it.
        unansweredSince: z.string().datetime().nullable(),
        // Same, but Vekha left it to the manager (manager's stage, out of Yana's scope).
        managerTurnSince: z.string().datetime().nullable(),
        // What she decided that day, in words (Паша 09.10: «а что за решение?»).
        decisions: z.array(z.object({
            kind: z.enum(["closed_lost", "closed_won", "stage", "handoff", "no_reply", "next_check"]),
            text: z.string().max(400).nullable(),
            at: z.string().datetime().nullable(),
            dueAt: z.string().datetime().nullable(),
        }).strict()).max(6),
        // Only what still stands at the end of the day: a stop or failure that a
        // later successful turn overtook is not shown.
        stop: z.object({ code: z.string().min(1).max(200), at: z.string().datetime() }).strict().nullable(),
        failure: z.object({
            kind: z.enum(["message", "crm", "preparation"]),
            operation: z.string().max(100).nullable(),
            code: z.string().max(200).nullable(),
            channelLabel: z.string().max(300).nullable(),
            at: z.string().datetime().nullable(),
        }).strict().nullable(),
        nextCheckScheduledFor: z.string().datetime().nullable(),
        attention: z.array(YanaDayAttentionSchema).max(4),
    }).strict(),
}).strict();
export const YanaDaySchema = z.object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/u),
    timezone: z.literal("Europe/Moscow"),
    from: z.string().datetime(),
    to: z.string().datetime(),
    items: z.array(YanaDayDealSchema).max(1_000),
    // Yana's chats with activity that day but no confirmed deal link; not listable.
    unlinkedConversationCount: z.number().int().nonnegative(),
    asOf: z.string().datetime(),
}).strict();
export const CrmDealDetailSchema = CrmDealSummarySchema.extend({
    activeConversations: z.array(z.object({
        id: z.string().uuid(),
        revision: z.number().int().nonnegative(),
        channel: CrmDealChannelSchema.pick({ key: true, label: true }),
        status: z.enum([
            "needs_response",
            "awaiting_review",
            "waiting_customer",
            "handoff_required",
            "closed",
        ]),
        blockReasonCode: z.string().trim().min(1).max(200).nullable(),
        /** 364: the stop of a chat whose channel was rejected; not the deal's stop. */
        rejectedChannelStop: z.boolean().optional(),
        lastMessageAt: z.string().datetime().nullable(),
        lastMessage: z.object({
            direction: z.enum(["inbound", "outbound"]),
            actorKind: z.enum(["customer", "operator", "ai", "provider", "system"]),
            authorName: z.string().trim().min(1).max(200).nullable(),
            content: z.string().min(1).max(4_000),
            occurredAt: z.string().datetime(),
        }).strict().nullable(),
    }).strict()).max(100),
    ambiguousConversations: z.array(z.object({
        id: z.string().uuid(),
        channel: CrmDealChannelSchema.pick({ key: true, label: true }),
        // A previously ambiguous link can retain one candidate after CRM reconciliation.
        // Until operator confirmation it is not an active exact link.
        candidateDealCount: z.number().int().min(1).max(100),
        lastMessage: z.object({
            direction: z.enum(["inbound", "outbound"]),
            actorKind: z.enum(["customer", "operator", "ai", "provider", "system"]),
            content: z.string().min(1).max(4_000),
            occurredAt: z.string().datetime(),
        }).strict().nullable(),
    }).strict()).max(100),
    escalations: z.array(z.object({
        id: z.string().uuid(),
        conversationId: z.string().uuid(),
        state: z.enum(["pending", "dispatching", "retryable", "delivered"]),
        blocking: z.boolean(),
        reason: z.string().trim().min(1).max(2_000),
        question: z.string().trim().min(1).max(2_000),
        recommendedAction: z.string().trim().min(1).max(2_000).nullable(),
        proposalSummary: z.string().trim().min(1).max(4_000),
        createdAt: z.string().datetime(),
        deliveredAt: z.string().datetime().nullable(),
    }).strict()).max(100).default([]),
}).strict();
export const OwnerEscalationAdviceMutationSchema = z.object({
    guidance: z.string().trim().min(1).max(4_000),
    idempotencyKey: z.string().trim().min(16).max(160),
}).strict();
export const OwnerEscalationAdviceResultSchema = z.object({
    escalationId: z.string().uuid(),
    conversationId: z.string().uuid(),
    resolutionAction: z.enum(["resume", "wait"]),
    jobId: z.string().uuid().nullable(),
    replayed: z.boolean(),
}).strict();
export const CrmDealMessageMediaSchema = z.object({
    id: z.string().uuid(),
    kind: z.enum(["image", "audio", "video", "document", "vcard", "geo", "sticker"]),
    providerType: z.string().trim().min(1).max(100),
    state: z.enum(["pending", "processing", "ready", "failed"]),
    fileName: z.string().trim().min(1).max(200),
    contentUrl: z.string().trim().min(1).max(500),
    analysisText: z.string().trim().min(1).max(8_000).nullable(),
    /** The file itself could not be opened; the dialogue went on without it. */
    unreadableReason: z.enum(["too_large", "unsupported_type", "link_refused"])
        .nullable().default(null),
}).strict();
export const CrmDealTimelineItemSchema = z.object({
    id: z.string().uuid(),
    conversationId: z.string().uuid(),
    channel: CrmDealChannelSchema.pick({ key: true, label: true }),
    sourceLabel: z.string().trim().min(1).max(300),
    direction: z.enum(["inbound", "outbound"]),
    actorKind: z.enum(["customer", "operator", "ai", "provider", "system"]),
    authorName: z.string().trim().min(1).max(200).nullable(),
    content: z.string().min(1).max(4_000),
    occurredAt: z.string().datetime(),
    media: CrmDealMessageMediaSchema.nullable().default(null),
    leadMagnetRequests: z.array(z.object({
        id: z.string().uuid(),
        magnetId: z.string().uuid(),
        title: z.string().min(3).max(200),
        phrase: z.string().min(2).max(100),
    }).strict()).optional(),
}).strict();
export const CrmDealTimelineSchema = z.object({
    dealId: z.string().uuid(),
    items: z.array(CrmDealTimelineItemSchema).max(100),
    asOf: z.string().datetime(),
    nextCursor: z.string().max(1_000).nullable(),
}).strict();
export const CrmDealReconciliationMutationSchema = z.object({
    employeeId: z.string().uuid(),
    reason: z.string().trim().min(5).max(1_000),
    idempotencyKey: z.string().trim().min(16).max(160),
    limit: z.number().int().min(1).max(1_000).default(500),
}).strict();
export const CrmDealReconciliationResultSchema = z.object({
    runId: z.string().uuid(),
    queuedConversationCount: z.number().int().nonnegative(),
    replayed: z.boolean(),
}).strict();
export const CrmDealLinkResolutionMutationSchema = z.object({
    conversationId: z.string().uuid(),
    reason: z.string().trim().min(5).max(1_000),
    idempotencyKey: z.string().trim().min(16).max(160),
}).strict();
export const CrmDealLinkResolutionResultSchema = z.object({
    dealId: z.string().uuid(),
    conversationId: z.string().uuid(),
    linkId: z.string().uuid(),
    replayed: z.boolean(),
}).strict();
export const CrmDealNoReplyMutationSchema = z.object({
    conversationId: z.string().uuid(),
    messageId: z.string().uuid(),
    expectedConversationRevision: z.number().int().nonnegative(),
    expectedDealRevision: z.number().int().nonnegative(),
    expectedReplyFence: z.number().int().nonnegative(),
    reason: z.string().trim().min(5).max(1_000),
    idempotencyKey: z.string().trim().min(16).max(160),
}).strict();
export const CrmDealNoReplyResultSchema = z.object({
    dealId: z.string().uuid(),
    conversationId: z.string().uuid(),
    messageId: z.string().uuid(),
    dealRevision: z.number().int().nonnegative(),
    conversationRevision: z.number().int().nonnegative(),
    replyFence: z.number().int().nonnegative(),
    replayed: z.boolean(),
}).strict();
export * from "./team-access.js";
export * from "./team-readiness.js";
export function semanticCorpusProblems(corpus) {
    const problems = [];
    for (const item of corpus.cases) {
        if (!item.semanticExpectation || !["anonymized_motif", "synthetic"].includes(item.sourceKind) || item.messages.length > 6)
            problems.push("Use short anonymized motifs or explicitly synthetic cases with expectations.");
        const text = [item.title, item.sourceRef, item.expectedResponse ?? "", item.expectedBehavior ?? "", JSON.stringify(item.semanticExpectation), corpus.reason, ...item.messages.map(x => x.text)].join("\n");
        if (/(?:https?:\/\/|[\w.+-]+@[\w.-]+\.[a-z]{2,}|(?:\+?\d[\s()-]*){10,}|@[a-z][a-z0-9_]{4,})/iu.test(text))
            problems.push("Remove contact identifiers, addresses and links from semantic fixtures.");
    }
    return problems;
}
export const SemanticFixturePackSchema = z.object({
    schemaVersion: z.literal("semantic_fixture_pack_v1"),
    corpus: HistoricalCaseImportMutationSchema,
    toolSnapshot: HistoricalReplayToolSnapshotSchema.nullable(),
}).strict().superRefine((pack, context) => {
    for (const message of semanticCorpusProblems(pack.corpus))
        context.addIssue({ code: "custom", message });
});
// Independent test deals; only configuration IDs may refer to real Vekha metadata.
export const SandboxBookingPolicySchema = z.object({
    targetStageId: z.string().nullable(), assignSelectedManager: z.boolean(),
    appointmentDateFieldId: z.number().nullable(), task: z.object({ text: z.string(), taskTypeId: z.number() }).nullable(),
    reminders: z.array(z.object({ offsetMinutesBefore: z.number().int().nonnegative(), message: z.string() }).strict()).max(10),
}).strict();
export const SandboxWorkflowSchema = z.object({
    id: z.string().uuid(), name: z.string().max(200), versionId: z.string().uuid(), digest: z.string().regex(/^[a-f0-9]{64}$/u),
    nodes: z.array(z.object({ id: z.string().uuid(), key: z.string(), stageId: z.string(), name: z.string(),
        kind: z.enum(["work", "handoff", "terminal"]), content: z.record(z.string(), z.unknown()),
    }).strict()).min(1).max(100),
    transitions: z.array(z.object({ source: z.string(), outcome: z.string(), disposition: z.enum(["node", "handoff", "terminal"]), target: z.string().nullable() }).strict()).max(500),
    bookingPolicy: SandboxBookingPolicySchema.nullable().default(null),
    mediaAssets: z.array(z.object({ assetId: z.string().uuid(), title: z.string(), mediaKind: z.enum(["audio", "image", "video", "document"]), fileName: z.string() }).strict()).max(100).default([]),
}).strict();
export const SandboxAttributesSchema = z.object({
    customerName: z.string().trim().min(1).max(80), request: z.string().trim().max(1000),
    timezone: z.enum(["Europe/Moscow", "UTC"]), experience: z.enum(["unknown", "new", "returning"]),
}).strict();
export const SandboxAgentSchema = z.object({
    id: z.string().regex(/^[a-z][a-z0-9_-]{0,63}$/u), name: z.string().trim().min(1).max(200),
}).strict();
export const SandboxCreateSchema = z.object({ idempotencyKey: z.string().uuid(), scenarioVersionId: z.string().uuid(),
    startNodeId: z.string().uuid(), title: z.string().trim().min(2).max(120), attributes: SandboxAttributesSchema,
    agentId: SandboxAgentSchema.shape.id.default("yana"),
}).strict();
export const SandboxTurnInputSchema = z.object({ idempotencyKey: z.string().uuid(), expectedRevision: z.number().int().nonnegative(),
    message: z.string().trim().min(1).max(4000), resume: z.boolean().default(false),
}).strict();
export const SandboxStateSchema = z.object({
    nodeId: z.string().uuid(), status: z.enum(["active", "handoff", "closed"]), revision: z.number().int().nonnegative(),
    attributes: SandboxAttributesSchema,
    stageHistory: z.array(z.object({ nodeId: z.string().uuid(), name: z.string(), at: z.string().datetime() }).strict()).max(201),
    bookings: z.array(z.object({ ref: z.string(), service: z.string(), time: z.string(), status: z.enum(["booked", "cancelled"]) }).strict()).max(100),
    nextCheck: z.object({ id: z.string().uuid(), dueAt: z.string().datetime(), intention: z.string() }).strict().nullable(),
    followUps: z.array(z.object({ id: z.string().uuid(), dueAt: z.string().datetime(), message: z.string(), status: z.enum(["scheduled", "cancelled"]), bookingRef: z.string().optional() }).strict()).max(100),
    memory: z.record(z.string(), z.string()), fields: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
    responsible: z.string().nullable(), serviceConversation: z.boolean(),
    media: z.array(z.object({ assetId: z.string(), caption: z.string().nullable() }).strict()).max(100),
}).strict();
export const SandboxLedgerSchema = z.array(z.object({ type: z.string(), label: z.string(), status: z.literal("simulated") }).strict()).max(50);
export const SandboxTurnSchema = z.object({ id: z.string().uuid(), request: SandboxTurnInputSchema,
    at: z.string().datetime(), reply: z.string(), proposal: DraftProposalSchema, actions: SandboxLedgerSchema,
    nextState: SandboxStateSchema, source: z.enum(["yana", "openclaw", "fixed_response_automation"]),
}).strict();
export const SandboxDealSchema = z.object({ id: z.string().uuid(), title: z.string(), createdAt: z.string().datetime(),
    agent: SandboxAgentSchema.default({ id: "yana", name: "Яна" }),
    workflow: SandboxWorkflowSchema, state: SandboxStateSchema, turns: z.array(SandboxTurnSchema).max(100),
}).strict();
export const SandboxCompletionRequestSchema = z.object({ protocol: z.literal("vekha_sandbox_v1"), scope: z.literal("sandbox:complete"),
    organizationId: z.string().uuid(), idempotencyKey: z.string().uuid(), deal: SandboxDealSchema,
    turn: SandboxTurnInputSchema, messageId: z.string().uuid(), now: z.string().datetime(),
}).strict();
// No markdown extraction, coercion, or unknown-key stripping at the model boundary.
export const SandboxProposalEnvelopeSchema = z.object({ proposal: DraftProposalObjectSchema }).strict();
export function parseSandboxProposal(text) {
    const raw = JSON.parse(text);
    const parsed = SandboxProposalEnvelopeSchema.parse(raw);
    // zod's existing Vekha proposal schema defaults optional legacy fields. Reject
    // unknown keys recursively using its output, while permitting those defaults.
    const known = (input, output) => {
        if (input && typeof input === "object") {
            for (const key of Object.keys(input)) {
                if (!output || typeof output !== "object" || !Object.hasOwn(output, key))
                    throw new Error("Unknown proposal field");
                known(input[key], output[key]);
            }
        }
    };
    known(raw, parsed);
    return parsed.proposal;
}
/** Booking route is independent of acquisition attribution and the current editor. */
export function calendarBookingSource(origin) {
    switch (origin) {
        case "yana": return { bookingPath: "yana", bookingSourceDetail: "Яна", label: "Яна" };
        case "miniapp": return { bookingPath: "selfbooking", bookingSourceDetail: "Самозапись", label: "Самозапись" };
        case "yulia": return { bookingPath: "manual", bookingSourceDetail: "Юля", label: "Ручная запись · Юля" };
        case "leader": return { bookingPath: "manual", bookingSourceDetail: "leader", label: "Ручная запись · руководитель" };
        case "manager": return { bookingPath: "manual", bookingSourceDetail: "manager", label: "Ручная запись · менеджер" };
        default: return { bookingPath: "unknown", bookingSourceDetail: "", label: "Неизвестно" };
    }
}
/** Owner-service operations reuse operator contracts, never arbitrary URLs or SQL. */
const OWNER_AGENT_OPERATIONS = [
    z.object({ operation: z.literal("lead_magnet_list") }).strict(),
    z.object({ operation: z.literal("lead_magnet_save"), input: LeadMagnetSaveSchema }).strict(),
    z.object({ operation: z.literal("lead_magnet_state"), magnetId: z.string().uuid(), input: LeadMagnetStateMutationSchema }).strict(),
    z.object({ operation: z.literal("lead_magnet_detail"), magnetId: z.string().uuid(), period: LeadMagnetPeriodSchema }).strict(),
    z.object({ operation: z.literal("retry_first_contact"), effectId: z.string().uuid(), expectedRevision: z.number().int().nonnegative(), expectedManagerFence: z.number().int().nonnegative(), idempotencyKey: z.string().min(16).max(160), reason: z.string().trim().min(5).max(1000) }).strict(),
    z.object({ operation: z.literal("return_options"), conversationId: z.string().uuid() }).strict(),
    z.object({ operation: z.literal("return_to_yana"), conversationId: z.string().uuid(), input: OwnerLeadReturnMutationSchema }).strict(),
    z.object({ operation: z.literal("deals"), query: CrmDealListQuerySchema }).strict(),
    z.object({ operation: z.literal("deal"), dealId: z.string().uuid() }).strict(),
    z.object({ operation: z.literal("timeline"), dealId: z.string().uuid(), query: CrmDealTimelineQuerySchema }).strict(),
    z.object({ operation: z.literal("conversation"), conversationId: z.string().uuid() }).strict(),
    z.object({ operation: z.literal("recover"), conversationId: z.string().uuid(), input: ConversationRecoverySchema }).strict(),
    z.object({ operation: z.literal("control"), conversationId: z.string().uuid(), input: ConversationControlMutationSchema }).strict(),
    z.object({ operation: z.literal("reconcile"), input: CrmDealReconciliationMutationSchema }).strict(),
    z.object({ operation: z.literal("resolve_link"), dealId: z.string().uuid(), input: CrmDealLinkResolutionMutationSchema }).strict(),
    // Рассылки офферов (docs/campaigns/DESIGN.md). Start only after the owner's explicit yes.
    z.object({ operation: z.literal("campaign_catalog") }).strict(),
    z.object({ operation: z.literal("campaign_cohort_preview"), input: CampaignCohortPreviewInputSchema }).strict(),
    z.object({ operation: z.literal("campaign_create"), input: CampaignCreateInputSchema }).strict(),
    z.object({ operation: z.literal("campaign_list") }).strict(),
    z.object({ operation: z.literal("campaign_status"), campaignId: z.string().uuid(), query: CampaignDetailQuerySchema.optional() }).strict(),
    z.object({ operation: z.literal("campaign_samples"), campaignId: z.string().uuid(), input: CampaignSamplesMutationSchema }).strict(),
    z.object({ operation: z.literal("campaign_state"), campaignId: z.string().uuid(), input: CampaignStateMutationSchema }).strict(),
    // Before the start only: change a draft or scheduled campaign; delete one that never sent anything.
    z.object({ operation: z.literal("campaign_update"), campaignId: z.string().uuid(), input: CampaignEditInputSchema }).strict(),
    z.object({ operation: z.literal("campaign_delete"), campaignId: z.string().uuid(), input: CampaignDeleteMutationSchema }).strict(),
    // «Команда» (Ден 19.10.2026): the host's `hypno-team add` asks Vekha for the checklist, the invitation and a training
    // booking of a new manager (ops/bin/team-onboarding). The invitation goes to the person's mail only.
    z.object({ operation: z.literal("team_readiness") }).strict(),
    // Without a group: a manager of sales into «Консультанты»; with one (Ден 21.10.2026): anyone into that access group.
    z.object({ operation: z.literal("team_invite"), name: z.string().trim().min(1).max(200), group: z.string().trim().min(2).max(120).optional() }).strict(),
    z.object({ operation: z.literal("team_training_booking"), name: z.string().trim().min(1).max(200), leadId: z.string().regex(/^[1-9]\d{0,19}$/u) }).strict(),
];
// One-time attachment of one chat (26.10.2026): a jpg, png or pdf the owner allowed for this exact client, sent by
// Yana's own turn once into this conversation only (migration 405). The basis is an answered owner escalation of the
// same conversation and deal; ownerMessageRef names the owner's message when there is one (telegram:4197).
export const CHAT_ATTACHMENT_CONTENT_TYPES = ["image/jpeg", "image/png", "application/pdf"];
export const CHAT_ATTACHMENT_MAX_BYTES = 10_485_760;
export const ChatAttachmentContentTypeSchema = z.enum(CHAT_ATTACHMENT_CONTENT_TYPES);
const ChatAttachmentBasisShape = {
    conversationId: z.string().uuid(),
    dealId: z.string().uuid(),
    basisEscalationId: z.string().uuid(),
    ownerMessageRef: z.string().trim().min(1).max(200).optional(),
    caption: z.string().trim().min(2).max(200)
        .describe("Что это за файл, одной фразой: так его увидит Яна в каталоге send_media этого чата."),
};
const Sha256HexSchema = z.string().trim().toLowerCase().regex(/^[0-9a-f]{64}$/u);
/** What Yana's tool and the host command take: a file on the host, never its bytes. */
export const ChatAttachmentFileInputSchema = z.object({
    ...ChatAttachmentBasisShape,
    path: z.string().min(2).max(1000)
        .describe("Абсолютный путь к jpg, png или pdf в data/ рабочей папки Яны или Аркадия."),
    contentSha256: Sha256HexSchema.describe("Ожидаемый SHA-256 файла."),
}).strict();
export const ChatAttachmentAddOperationSchema = z.object({
    operation: z.literal("chat_attachment_add"),
    ...ChatAttachmentBasisShape,
    fileName: z.string().trim().min(1).max(240),
    contentType: ChatAttachmentContentTypeSchema,
    contentSha256: Sha256HexSchema,
    contentBase64: CompanyMediaAssetMutationSchema.shape.contentBase64,
}).strict();
export const ChatAttachmentAddResultSchema = z.object({
    mediaAssetId: z.string().uuid(),
    conversationId: z.string().uuid(),
    contentSha256: z.string().regex(/^[0-9a-f]{64}$/u),
    byteSize: z.number().int().positive(),
    replayed: z.boolean(),
}).strict();
/** Operations an owner agent passes as they are. */
export const OwnerAgentOperationsInputSchema = z.discriminatedUnion("operation", OWNER_AGENT_OPERATIONS);
/** The signed wire of /api/owner/operations: the agent operations and a chat attachment read on the host. */
export const OwnerOperationsInputSchema = z.discriminatedUnion("operation", [
    ...OWNER_AGENT_OPERATIONS,
    ChatAttachmentAddOperationSchema,
]);
export * from "./bot-campaigns.js";
export * from "./owner-new-application.js";
export * from "./site-forms.js";
//# sourceMappingURL=index.js.map